import { useEffect, useState } from "react";
import { Crown, Sparkles, TrendingUp, Lock, RefreshCw, Edit2, Trash2, Search, Filter, Calendar, Heart, CheckCircle, Gavel, Gift, Ticket } from "lucide-react";
import { GoldCoin, FollisCoin } from "@/components/CoinIcons";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Loader, btn } from "@/components/Page";
import Modal from "@/components/Modal";
import ConfirmDialog from "@/components/ConfirmDialog";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import BeastIcon from "@/components/BeastIcon";

const TIER_COLORS = {
  silver: "border-slate-400 bg-slate-900/20 text-slate-300",
  elite: "border-purple-500 bg-purple-900/20 text-purple-300",
  legendary: "border-amber-500 bg-amber-900/20 text-amber-300"};

const UPGRADE_COSTS = {
  3: { capacity: 5, cost: "300 Uncommon Fragments" },
  5: { capacity: 8, cost: "200 Rare Fragments" },
  8: { capacity: 10, cost: "500 Rare Fragments" },
  10: { capacity: 12, cost: "200 Elite Fragments" },
  12: { capacity: 15, cost: "100 Legendary Fragments" },
  15: { capacity: 20, cost: "500 Gold" },
  20: { capacity: 25, cost: "500 Gold" },
  25: { capacity: 30, cost: "500 Gold" },
  30: { capacity: 35, cost: "500 Gold" },
  35: { capacity: 40, cost: "500 Gold" },
  40: { capacity: 45, cost: "500 Gold" },
  45: { capacity: 50, cost: "500 Gold" }};

const VIVARIUM_UPKEEP_FOLLIS = {
  silver: 25,
  elite: 50,
  legendary: 100,
};
const BEAST_AFFIX_CAP = 8;
const LEGENDARY_CUSTOM_AFFIX_OPTIONS = ["strength", "speed", "defense", "stamina", "technique", "precision"];

export default function Vivarium() {
  const { user, refreshUser } = useAuth();
  const [data, setData] = useState(null);
  const [consumables, setConsumables] = useState({});
  const [busy, setBusy] = useState(false);
  const [buildModal, setBuildModal] = useState(false);
  const [upgradeModal, setUpgradeModal] = useState(false);
  const [feedModal, setFeedModal] = useState(false);
  const [clearDebtDays, setClearDebtDays] = useState(0);
  const [beastFoodModal, setBeastFoodModal] = useState(null); // {beast, food_type}
  const [beastSupplementModal, setBeastSupplementModal] = useState(null); // {beast, selectedAffix}
  const [lovePotionModal, setLovePotionModal] = useState(null); // beast
  const [selectedAffix, setSelectedAffix] = useState(null); // Track which affix user wants to boost
  const [renameModal, setRenameModal] = useState(null); // {beast_id, current_name}
  const [equipConfirm, setEquipConfirm] = useState(null); // beast to equip (confirmation step)
  const [equipModal, setEquipModal] = useState(null); // beast to equip (gladiator picker)
  const [releaseConfirm, setReleaseConfirm] = useState(null); // beast to release
  const [breedModal, setBreedModal] = useState(false); // breeding modal
  const [breedParent1, setBreedParent1] = useState(null); // first parent selected
  const [breedParent2, setBreedParent2] = useState(null); // second parent selected
  const [breedUseTickets, setBreedUseTickets] = useState(false); // payment method
  const [breedForceMutation, setBreedForceMutation] = useState(false); // admin: force mutation
  const [breedConfirmModal, setBreedConfirmModal] = useState(false); // final confirmation
  const [breedResult, setBreedResult] = useState(null); // breeding result to display
  const [breedSortBy, setBreedSortBy] = useState("species"); // sorting in breed modal: species, rarity, stat
  const [sortBy, setSortBy] = useState("date"); // date, species, rarity, stat
  const [filterStatus, setFilterStatus] = useState("all"); // all, equipped, unequipped
  const [beastPage, setBeastPage] = useState(1); // pagination for beast display
  const BEASTS_PER_PAGE = 15;
  const [newName, setNewName] = useState("");
  const [gladiators, setGladiators] = useState(null); // for equip modal
  const [purchaseConfirm, setPurchaseConfirm] = useState(null); // ticket purchase confirmation
  const [legendaryAffixModal, setLegendaryAffixModal] = useState(null); // beast
  const [legendaryAffixStat, setLegendaryAffixStat] = useState(null);
  const [legendaryAffixConfirm, setLegendaryAffixConfirm] = useState(null); // { beast, stat }
  const supplementPrimaryAtCap = Number(beastSupplementModal?.beast?.affix_value || 0) >= BEAST_AFFIX_CAP;
  const supplementSecondaryAtCap = Number(beastSupplementModal?.beast?.affix_value_2 || 0) >= BEAST_AFFIX_CAP;
  const selectedSupplementAtCap = selectedAffix === beastSupplementModal?.beast?.affix_stat
    ? supplementPrimaryAtCap
    : selectedAffix === beastSupplementModal?.beast?.affix_stat_2 && supplementSecondaryAtCap;
  
  // Fragment donation split between weapon/armor for build/upgrade
  // Fragment donation split between weapon/armor for build/upgrade
  const [buildFragments, setBuildFragments] = useState({ 
    common_weapon: 500, 
    common_armor: 0,
    uncommon_weapon: 100,
    uncommon_armor: 0
  });
  const [upgradeFragments, setUpgradeFragments] = useState({});
  
  // Initialize upgrade fragments when opening modal
  const openUpgradeModal = () => {
    if (data?.next_upgrade?.costs) {
      const initial = {};
      Object.keys(data.next_upgrade.costs).forEach(type => {
        // For each rarity (e.g., "uncommon"), initialize weapon and armor to 0
        initial[`${type}_weapon`] = data.next_upgrade.costs[type];
        initial[`${type}_armor`] = 0;
      });
      setUpgradeFragments(initial);
    }
    setUpgradeModal(true);
  };

  const load = async () => {
    try {
      const { data } = await api.get("/vivarium");
      setData(data);
    } catch (e) {
      toast.error(apiErr(e));
    }
  };

  const loadConsumables = async () => {
    try {
      const { data } = await api.get("/inventory");
      // Convert consumables array to object for easy lookup
      const consumablesMap = {};
      (data.consumables || []).forEach(item => {
        consumablesMap[item.id] = item.count;
      });
      setConsumables(consumablesMap);
    } catch (e) {
      // Silently fail - consumables are optional
    }
  };

  const getRemainingCooldownDays = (expiresAt) => {
    if (!expiresAt) return 0;
    return Math.max(0, Math.ceil((new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24)));
  };

  useEffect(() => { 
    load(); 
    loadConsumables();
  }, []);

  const buildVivarium = async () => {
    setBusy(true);
    try {
      await api.post("/vivarium/build", { fragments: buildFragments });
      toast.success("Vivarium built! You can now capture beasts.");
      setBuildModal(false);
      await load();
      refreshUser && refreshUser();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const upgradeVivarium = async () => {
    setBusy(true);
    try {
      const { data: result } = await api.post("/vivarium/upgrade", { fragments: upgradeFragments });
      toast.success(`Vivarium upgraded to ${result.new_capacity} beast capacity!`);
      setUpgradeModal(false);
      await load();
      refreshUser && refreshUser();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const openFeedModal = () => {
    const unpaidDays = data?.unpaid_upkeep_dates?.length || data?.vivarium?.unpaid_upkeep_days || 0;
    const todayPaid = !!data?.upkeep_today_paid;
    setClearDebtDays(todayPaid && unpaidDays > 0 ? 1 : 0);
    setFeedModal(true);
  };

  const feedWorkers = async (selectedDebtDays) => {
    setBusy(true);
    try {
      const { data: result } = await api.post("/vivarium/feed", { clear_unpaid_days: selectedDebtDays });
      toast.success(`Workers paid. Covered ${result.paid_days} upkeep day${result.paid_days === 1 ? "" : "s"}.`);
      setFeedModal(false);
      await load();
      refreshUser && refreshUser();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const toggleAutoFeed = async (enabled) => {
    setBusy(true);
    try {
      await api.patch("/vivarium/auto-feed", { enabled });
      if (!enabled) {
        toast.success("Automatic feeding disabled.");
      } else {
        toast.success("Automatic feeding enabled. The server will check upkeep once per day and try auto-pay first.");
      }
      await load();
      refreshUser && refreshUser();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const renameBeast = async () => {
    if (!renameModal || !newName.trim()) return;
    setBusy(true);
    try {
      const { data: result } = await api.post("/vivarium/beast/rename", { beast_id: renameModal.id, custom_name: newName.trim() });
      toast.success(result.follis_spent ? `Beast renamed · ${result.follis_spent} Follis spent` : "Beast renamed free of charge");
      setRenameModal(null);
      setNewName("");
      await load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const openLegendaryAffixPicker = (beast) => {
    setLegendaryAffixModal(beast);
    setLegendaryAffixStat(null);
  };

  const assignLegendaryAffix = async () => {
    if (!legendaryAffixConfirm) return;
    setBusy(true);
    try {
      const { data: result } = await api.post("/vivarium/beast/legendary-affix", {
        beast_id: legendaryAffixConfirm.beast.id,
        affix_stat: legendaryAffixConfirm.stat,
      });
      toast.success(result.message);
      setLegendaryAffixConfirm(null);
      await load();
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setBusy(false);
    }
  };

  const releaseBeast = async () => {
    if (!releaseConfirm) return;
    setBusy(true);
    try {
      await api.post("/vivarium/beast/release", { beast_id: releaseConfirm.id });
      toast.success(`${releaseConfirm.display_name} released.`);
      setReleaseConfirm(null);
      await load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const feedBeastFood = async () => {
    if (!beastFoodModal) return;
    setBusy(true);
    try {
      const { data: result } = await api.post(`/vivarium/beast/feed`, { 
        beast_id: beastFoodModal.beast.id,
        food_type: beastFoodModal.food_type 
      });
      toast.success(result.message, { icon: "🍖" });
      setBeastFoodModal(null);
      await load();
      await loadConsumables();
      refreshUser && refreshUser();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const useBeastSupplement = async () => {
    if (!beastSupplementModal || !selectedAffix) return;
    setBusy(true);
    try {
      const { data: result } = await api.post(`/vivarium/beast/supplement`, { 
        beast_id: beastSupplementModal.beast.id,
        affix_stat: selectedAffix
      });
      toast.success(result.message, { icon: "⚗️" });
      setBeastSupplementModal(null);
      setSelectedAffix(null);
      await load();
      await loadConsumables();
      refreshUser && refreshUser();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const useLovePotion = async () => {
    if (!lovePotionModal) return;
    setBusy(true);
    try {
      const { data: result } = await api.post(`/vivarium/beast/love-potion`, {
        beast_id: lovePotionModal.id,
      });
      toast.success(result.message, { icon: "💙" });
      setLovePotionModal(null);
      await load();
      await loadConsumables();
      refreshUser && refreshUser();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const equipBeast = async (gladiatorId) => {
    if (!equipModal) return;
    setBusy(true);
    try {
      await api.post("/vivarium/beast/equip", { beast_id: equipModal.id, gladiator_id: gladiatorId });
      toast.success(`${equipModal.display_name} equipped!`, { icon: <BeastIcon size={18} className="text-amber-400" /> });
      setEquipModal(null);
      setGladiators(null);
      await load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  const loadGladiators = async () => {
    try {
      const { data: result } = await api.get("/gladiators");
      setGladiators(result.active || []);
    } catch (e) {
      toast.error(apiErr(e));
      setGladiators([]);
    }
  };

  // Load gladiators when equip modal opens
  useEffect(() => {
    if (equipModal && !gladiators) {
      loadGladiators();
    }
  }, [equipModal]);
  
  const confirmEquip = (beast) => {
    setEquipConfirm(beast);
  };
  
  const proceedToEquip = () => {
    setEquipModal(equipConfirm);
    setEquipConfirm(null);
  };
  
  const startBreeding = () => {
    setBreedParent1(null);
    setBreedParent2(null);
    setBreedModal(true);
  };
  
  const selectBreedParent = (beast) => {
    if (!breedParent1) {
      setBreedParent1(beast);
    } else if (!breedParent2) {
      // Check if same species
      if (beast.species !== breedParent1.species) {
        toast.error("Beasts must be the same species to breed");
        return;
      }
      if (beast.id === breedParent1.id) {
        toast.error("Cannot breed a beast with itself");
        return;
      }
      setBreedParent2(beast);
    }
  };
  
  const clearBreedParent = (parentNum) => {
    if (parentNum === 1) {
      setBreedParent1(null);
      setBreedParent2(null); // Reset both if clearing parent 1
    } else {
      setBreedParent2(null);
    }
  };
  
  const confirmBreeding = async () => {
    if (!breedParent1 || !breedParent2) return;
    
    setBusy(true);
    try {
      const { data } = await api.post("/vivarium/breed", {
        parent1_id: breedParent1.id,
        parent2_id: breedParent2.id,
        use_tickets: breedUseTickets,
        force_mutation: user?.is_admin && breedForceMutation  // Admin only
      });
      
      // Store result for display modal
      setBreedResult({
        ...data,
        parent1: breedParent1,
        parent2: breedParent2});
      
      setBreedConfirmModal(false);
      setBreedModal(false);
      setBreedParent1(null);
      setBreedParent2(null);
      setBreedUseTickets(false);
      setBreedForceMutation(false);
      await load();
      await refreshUser();
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };
  
  const claimQuest = async (questId) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/vivarium/quests/claim/${questId}`);
      toast.success(`Quest complete! +${data.tickets_awarded} breeding ticket${data.tickets_awarded > 1 ? 's' : ''}`, {
        icon: "🎟️"
      });
      await load();
    } catch (err) {
      toast.error(apiErr(err));
    } finally {
      setBusy(false);
    }
  };

  const unequipBeast = async (beastId) => {
    setBusy(true);
    try {
      await api.post("/vivarium/beast/unequip", { beast_id: beastId });
      toast.success("Beast unequipped and returned to Vivarium.");
      await load();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setBusy(false); }
  };

  if (!data) return <Loader />;

  const vivarium = data.vivarium;
  const beasts = data.beasts || [];
  const upkeepDays = data.unpaid_upkeep_dates?.length || vivarium.unpaid_upkeep_days || 0;
  const bonusesDisabled = upkeepDays >= 14;
  const autoFeedEnabled = !!vivarium.auto_feed_enabled;
  const autoFeedLastPaidLabel = vivarium.auto_feed_last_paid_at
    ? new Date(vivarium.auto_feed_last_paid_at).toLocaleString()
    : null;
  const autoFeedLastFailure = vivarium.auto_feed_last_failure;
  const autoFeedLastChargeFollis = Number(vivarium.auto_feed_last_charge_follis || 0);
  const upkeepTodayPaid = !!data.upkeep_today_paid;
  const upkeepTodayDue = !!data.upkeep_today_due;
  const unpaidUpkeepDates = data.unpaid_upkeep_dates || [];
  const upkeepBreakdown = data.upkeep_breakdown || [];
  const totalDueFollis = Number(data.total_due_follis || 0);
  const totalPayDays = (upkeepTodayDue ? 1 : 0) + clearDebtDays;
  const totalUpkeepFollis = Number(data.total_upkeep_follis || 0);
  const payPreviewFollis = totalUpkeepFollis * totalPayDays;
  const upkeepTierLines = Object.entries(VIVARIUM_UPKEEP_FOLLIS)
    .map(([tier, amount]) => {
      const count = beasts.filter((beast) => beast.tier === tier).length;
      return count > 0 ? { tier, count, amount, total: count * amount } : null;
    })
    .filter(Boolean);
  const quests = data.quests || [];
  const breedingTickets = data.breeding_tickets || 0;

  // Sort beasts
  let sortedBeasts = [...beasts];
  if (sortBy === "species") sortedBeasts.sort((a, b) => a.species.localeCompare(b.species));
  if (sortBy === "rarity") sortedBeasts.sort((a, b) => {
    const order = { legendary: 0, elite: 1, silver: 2 };
    return order[a.tier] - order[b.tier];
  });
  if (sortBy === "stat") sortedBeasts.sort((a, b) => b.primary_value - a.primary_value);
  if (sortBy === "date") sortedBeasts.sort((a, b) => new Date(b.acquired_at) - new Date(a.acquired_at));

  // Filter beasts
  if (filterStatus === "equipped") sortedBeasts = sortedBeasts.filter(b => b.equipped_to);
  if (filterStatus === "unequipped") sortedBeasts = sortedBeasts.filter(b => !b.equipped_to);

  // Pagination
  const totalBeasts = sortedBeasts.length;
  const totalPages = Math.ceil(totalBeasts / BEASTS_PER_PAGE);
  const startIdx = (beastPage - 1) * BEASTS_PER_PAGE;
  const endIdx = startIdx + BEASTS_PER_PAGE;
  const paginatedBeasts = sortedBeasts.slice(startIdx, endIdx);

  return (
    <div data-testid="vivarium-page">
      {/* Hero Banner */}
      <div className="relative mb-6 overflow-hidden border border-amber-900/40 rounded-sm">
        <img 
          src="/vivarium-bg.png" 
          alt="Vivarium" 
          className="w-full h-40 sm:h-52 object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <div className="flex items-center gap-3 mb-2">
            <BeastIcon size={28} className="text-amber-400" />
            <h1 className="text-3xl sm:text-4xl font-cinzel text-amber-300 uppercase tracking-wide">Vivarium</h1>
          </div>
          <p className="text-sm text-zinc-300">Capture, train, and equip exotic beasts to your gladiators</p>
        </div>
      </div>

      <div className="space-y-6">
      {!vivarium.built ? (
        <div className="max-w-2xl border border-zinc-800 bg-zinc-950 rounded-sm p-6 text-center" data-testid="build-vivarium-prompt">
          <BeastIcon size={48} className="text-amber-400 mx-auto mb-4" />
          <h2 className="text-xl font-cinzel text-amber-300 mb-2">Build a Vivarium</h2>
          <p className="text-sm text-zinc-400 mb-4">
            Construct a beast enclosure to capture and train wild creatures. Equip them to your gladiators for powerful stat bonuses in Ranked PvP.
          </p>
          <p className="text-xs text-zinc-500 mb-6">
            <b className="text-amber-300">Cost:</b> 500 Common Fragments + 100 Uncommon Fragments<br />
            <b className="text-amber-300">Starting Capacity:</b> 3 beasts
          </p>
          <button onClick={() => setBuildModal(true)} className={btn() + " flex items-center gap-2 mx-auto"}>
            <Sparkles size={16} /> Build Vivarium
          </button>
        </div>
      ) : (
        <>
          {/* Upkeep Status - Enhanced Display */}
          {bonusesDisabled && (
            <div className="border-2 border-rose-700 bg-rose-950/30 rounded-sm p-5" data-testid="upkeep-warning">
              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <p className="text-base text-rose-300 font-bold font-cinzel">🚫 BEASTS ARE STARVING!</p>
                  <p className="text-sm text-zinc-300 mt-2">
                    You currently owe <b className="text-rose-300">{upkeepDays} unpaid day{upkeepDays === 1 ? "" : "s"}</b>. All beast bonuses are <b className="text-rose-300">DISABLED</b> until that debt is reduced below 14 days.
                  </p>
                  <div className="mt-3 p-3 bg-zinc-900/50 rounded border border-rose-800/30">
                    <p className="text-xs text-zinc-400 font-semibold mb-2">⚠️ Consequences:</p>
                    <ul className="text-xs text-zinc-400 space-y-1">
                      <li>• All equipped beasts provide <b className="text-rose-400">ZERO stats</b> to gladiators</li>
                      <li>• Beasts cannot be bred or equipped</li>
                      <li>• After 14 days without food, beasts will <b className="text-rose-400">abandon you</b></li>
                    </ul>
                  </div>
                  <button 
                    onClick={openFeedModal} 
                    className="mt-4 px-4 py-2 bg-rose-600 hover:bg-rose-500 text-white rounded-sm font-semibold transition-colors"
                  >
                    Feed Beasts Now
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Upkeep Warning - Near Deadline */}
          {!bonusesDisabled && upkeepDays >= 10 && (
            <div className="border-2 border-amber-700 bg-amber-950/20 rounded-sm p-4">
              <div className="flex items-start gap-3">
                <Sparkles size={20} className="text-amber-400 shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm text-amber-300 font-semibold">⚠️ Beasts getting hungry!</p>
                  <p className="text-xs text-zinc-400 mt-1">
                    Your Vivarium is carrying <b className="text-amber-300">{upkeepDays} unpaid day{upkeepDays === 1 ? "" : "s"}</b>. Pay workers soon or lose beast bonuses in {14 - upkeepDays} days.
                  </p>
                  <button 
                    onClick={openFeedModal} 
                    className="mt-2 text-xs text-amber-400 hover:text-amber-300 underline"
                  >
                    Pay Upkeep Now
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Upkeep Reminder - Moderate */}
          {!bonusesDisabled && upkeepDays >= 5 && upkeepDays < 10 && (
            <div className="border border-amber-800/40 bg-amber-950/10 rounded-sm p-3">
              <div className="flex items-center gap-2">
                <Sparkles size={16} className="text-amber-400" />
                <p className="text-xs text-amber-300">
                  {upkeepDays} unpaid day{upkeepDays === 1 ? "" : "s"} currently owed. Clear debt before it snowballs.
                </p>
                <button 
                  onClick={openFeedModal} 
                  className="ml-auto text-xs text-amber-400 hover:text-amber-300 underline"
                >
                  Pay Upkeep
                </button>
              </div>
            </div>
          )}

          {/* Stats Row */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="border border-zinc-800 bg-zinc-950 p-4 rounded-sm">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5"><BeastIcon size={12} /> Beasts</div>
              <div className="text-2xl font-cinzel text-amber-300 mt-1">{data.beast_count}/{vivarium.capacity}</div>
            </div>
            <div className="border border-zinc-800 bg-zinc-950 p-4 rounded-sm">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1.5"><Ticket size={12} className="text-amber-400" /> Venatore Tickets</div>
              <div className="text-2xl font-cinzel text-amber-300 mt-1">{data.venatore_tickets}</div>
              <p className="text-[10px] text-zinc-400 mt-1">
                {data.pvp_battles_for_ticket || 0}/100 fights until next ticket
              </p>
              
              {/* One-time welcome ticket claim */}
              {data.can_claim_welcome_ticket && (
                <button
                  onClick={async () => {
                    try {
                      await api.post("/vivarium/claim-welcome-ticket");
                      toast.success('🎁 Free Venatore Ticket claimed!');
                      load();
                      refreshUser && refreshUser();
                    } catch (e) {
                      toast.error(apiErr(e));
                    }
                  }}
                  className="mt-2 w-full bg-gradient-to-r from-amber-600 to-amber-700 hover:from-amber-500 hover:to-amber-600 text-white text-xs py-1.5 px-3 rounded transition-all flex items-center justify-center gap-1.5"
                >
                  <Gift size={14} />
                  Claim Free Ticket!
                </button>
              )}
              
              {/* Purchase Venatore Ticket */}
              <div className="space-y-2">
                <button
                  onClick={() => {
                    setPurchaseConfirm({
                      title: "Purchase Venatore Ticket?",
                      message: `Spend 100 gold to purchase 1 Venatore Ticket?\n\nVenatore tickets are used to enter beast battles in the arena.`,
                      confirmLabel: "Purchase",
                      onConfirm: async () => {
                        try {
                          setBusy(true);
                          const { data: purchaseData } = await api.post("/vivarium/purchase-venatore-ticket");
                          toast.success(purchaseData.message);
                          load();
                          refreshUser && refreshUser();
                          setPurchaseConfirm(null);
                        } catch (e) {
                          toast.error(apiErr(e));
                        } finally {
                          setBusy(false);
                        }
                      }
                    });
                  }}
                  disabled={busy || (user?.gold || 0) < 100}
                  className={`w-full text-[10px] uppercase tracking-widest px-3 py-1.5 border transition-colors rounded-sm ${
                    (user?.gold || 0) >= 100
                      ? "border-amber-700 text-amber-300 hover:bg-amber-900/20"
                      : "border-zinc-800 text-zinc-600 cursor-not-allowed"
                  }`}
                >
                  <GoldCoin size={10} className="inline mr-1" />
                  Buy for 100 Gold
                </button>
                <button
                  onClick={() => {
                    setPurchaseConfirm({
                      title: "Purchase Venatore Ticket?",
                      message: `Spend 1000 follis to purchase 1 Venatore Ticket?\n\nVenatore tickets are used to enter beast battles in the arena.`,
                      confirmLabel: "Purchase",
                      onConfirm: async () => {
                        try {
                          setBusy(true);
                          const { data: purchaseData } = await api.post("/vivarium/purchase-venatore-ticket-follis");
                          toast.success(purchaseData.message);
                          load();
                          refreshUser && refreshUser();
                          setPurchaseConfirm(null);
                        } catch (e) {
                          toast.error(apiErr(e));
                        } finally {
                          setBusy(false);
                        }
                      }
                    });
                  }}
                  disabled={busy || (user?.follis || 0) < 1000}
                  className={`w-full text-[10px] uppercase tracking-widest px-3 py-1.5 border transition-colors rounded-sm ${
                    (user?.follis || 0) >= 1000
                      ? "border-amber-700 text-amber-400 hover:bg-amber-900/20"
                      : "border-zinc-800 text-zinc-600 cursor-not-allowed"
                  }`}
                >
                  <FollisCoin size={10} className="inline mr-1" />
                  Buy for 1000 Follis
                </button>
              </div>
            </div>
            <div className="border border-zinc-800 bg-zinc-950 p-4 rounded-sm">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Daily Upkeep</div>
              <div className="text-sm text-zinc-300 mt-1">
                {upkeepTierLines.map((line) => (
                  <div key={line.tier}>{line.count} {line.tier} tier · {line.total} follis/day</div>
                ))}
                {upkeepTierLines.length === 0 && <span className="text-zinc-400">None</span>}
              </div>
              <p className="text-[10px] text-zinc-500 mt-2" data-testid="vivarium-upkeep-follis-note">
                Daily upkeep uses follis only.
              </p>
            </div>
            <div className="border border-zinc-800 bg-zinc-950 p-4 rounded-sm">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Breeding Tickets</div>
              <div className="text-2xl font-cinzel text-amber-300 mt-1">🎟️ {breedingTickets}</div>
              <div className="space-y-2 mt-2">
                <button
                  onClick={() => {
                    setPurchaseConfirm({
                      title: "Purchase Breeding Ticket?",
                      message: `Spend 100 gold to purchase 1 Breeding Ticket?\n\nBreeding tickets let you breed beasts without paying the gold cost.`,
                      confirmLabel: "Purchase",
                      onConfirm: async () => {
                        try {
                          setBusy(true);
                          const { data: purchaseData } = await api.post("/vivarium/purchase-breeding-ticket");
                          toast.success(purchaseData.message);
                          load();
                          refreshUser && refreshUser();
                          setPurchaseConfirm(null);
                        } catch (e) {
                          toast.error(apiErr(e));
                        } finally {
                          setBusy(false);
                        }
                      }
                    });
                  }}
                  disabled={busy || (user?.gold || 0) < 100}
                  className={`w-full text-[10px] uppercase tracking-widest px-3 py-1.5 border transition-colors rounded-sm ${
                    (user?.gold || 0) >= 100
                      ? "border-amber-600 text-amber-300 hover:bg-amber-900/20"
                      : "border-zinc-800 text-zinc-600 cursor-not-allowed"
                  }`}
                >
                  <GoldCoin size={10} className="inline mr-1" />
                  Buy for 100 Gold
                </button>
                <button
                  onClick={() => {
                    setPurchaseConfirm({
                      title: "Purchase Breeding Ticket?",
                      message: `Spend 1000 follis to purchase 1 Breeding Ticket?\n\nBreeding tickets let you breed beasts without paying the gold cost.`,
                      confirmLabel: "Purchase",
                      onConfirm: async () => {
                        try {
                          setBusy(true);
                          const { data: purchaseData } = await api.post("/vivarium/purchase-breeding-ticket-follis");
                          toast.success(purchaseData.message);
                          load();
                          refreshUser && refreshUser();
                          setPurchaseConfirm(null);
                        } catch (e) {
                          toast.error(apiErr(e));
                        } finally {
                          setBusy(false);
                        }
                      }
                    });
                  }}
                  disabled={busy || (user?.follis || 0) < 1000}
                  className={`w-full text-[10px] uppercase tracking-widest px-3 py-1.5 border transition-colors rounded-sm ${
                    (user?.follis || 0) >= 1000
                      ? "border-amber-700 text-amber-400 hover:bg-amber-900/20"
                      : "border-zinc-800 text-zinc-600 cursor-not-allowed"
                  }`}
                >
                  <FollisCoin size={10} className="inline mr-1" />
                  Buy for 1000 Follis
                </button>
              </div>
            </div>
            <div className="border border-zinc-800 bg-zinc-950 p-4 rounded-sm">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Upkeep Status</div>
              <div className={`text-sm font-semibold mt-1 ${bonusesDisabled ? "text-rose-400" : "text-amber-300"}`}>
                {bonusesDisabled ? "🚫 Starving" : upkeepDays >= 10 ? `⚠️ Heavy Debt (${upkeepDays}d)` : upkeepDays >= 5 ? `😐 Debt Building (${upkeepDays}d)` : upkeepTodayDue ? "⏳ Today Unpaid" : upkeepDays > 0 ? `✓ Debt: ${upkeepDays}d` : "✓ Today Paid"}
              </div>
              <div className="text-[9px] text-zinc-400 mt-1">
                {bonusesDisabled ? "Bonuses disabled" : upkeepDays >= 10 ? `${14 - upkeepDays}d until disabled` : upkeepTodayDue ? "Workers still need today’s payment" : upkeepDays > 0 ? "Today is settled, but older debt remains" : "All bonuses active"}
              </div>
              {beasts.length > 0 && (
                <button 
                  onClick={openFeedModal}
                  disabled={busy}
                  className="w-full mt-3 text-[11px] font-bold uppercase tracking-widest px-3 py-2 border transition-all rounded-sm border-amber-600/80 text-amber-300 bg-amber-950/20 hover:bg-amber-900/30 hover:border-amber-400 hover:text-amber-200 text-center block disabled:opacity-50 disabled:cursor-not-allowed" 
                  data-testid="vivarium-feed-now-button"
                >
                  {upkeepTodayDue || upkeepDays > 0 ? "Pay Workers Now" : "Review Daily Upkeep"}
                </button>
              )}
              <div className="mt-3 grid grid-cols-2 gap-2 text-[10px] text-zinc-400" data-testid="vivarium-upkeep-summary-grid">
                <div className="border border-zinc-800 bg-zinc-900/40 px-2 py-2 rounded-sm">
                  <div className="uppercase tracking-widest text-zinc-500">Today</div>
                  <div className="mt-1 font-semibold text-amber-300" data-testid="vivarium-upkeep-today-state">
                    {upkeepTodayPaid ? "Paid" : "Due"}
                  </div>
                </div>
                <div className="border border-zinc-800 bg-zinc-900/40 px-2 py-2 rounded-sm">
                  <div className="uppercase tracking-widest text-zinc-500">Debt</div>
                  <div className={`mt-1 font-semibold ${upkeepDays > 0 ? "text-rose-300" : "text-zinc-300"}`} data-testid="vivarium-upkeep-debt-days">
                    {upkeepDays} day{upkeepDays === 1 ? "" : "s"}
                  </div>
                </div>
              </div>
              <div className="mt-4 border-t border-zinc-800 pt-3" data-testid="vivarium-auto-feed-panel">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-[10px] uppercase tracking-widest text-zinc-500" data-testid="vivarium-auto-feed-heading">Automatic Feeding</p>
                    <p className="mt-1 text-xs text-zinc-400" data-testid="vivarium-auto-feed-description">
                      When enabled, the server checks Vivarium upkeep once per day at midnight game time. It tries auto-pay first; if follis are short, that day becomes debt.
                    </p>
                  </div>
                  <Switch
                    checked={autoFeedEnabled}
                    onCheckedChange={toggleAutoFeed}
                    disabled={busy}
                    aria-label="Toggle automatic feeding"
                    data-testid="vivarium-auto-feed-switch"
                    className="data-[state=checked]:bg-amber-600 data-[state=unchecked]:bg-zinc-700"
                  />
                </div>
                <p className={`mt-2 text-[10px] uppercase tracking-widest ${autoFeedEnabled ? "text-amber-300" : "text-zinc-500"}`} data-testid="vivarium-auto-feed-state">
                  {autoFeedEnabled ? "Enabled" : "Disabled"}
                </p>
                {autoFeedEnabled && autoFeedLastPaidLabel && (
                  <p className="mt-1 text-[11px] text-zinc-400" data-testid="vivarium-auto-feed-last-paid">
                    Last automatic payment: {autoFeedLastPaidLabel}
                  </p>
                )}
                {autoFeedEnabled && autoFeedLastChargeFollis > 0 && (
                  <p className="mt-1 text-[11px] text-zinc-400" data-testid="vivarium-auto-feed-last-charge">
                    Last charge: {autoFeedLastChargeFollis} follis
                  </p>
                )}
                {autoFeedEnabled && autoFeedLastFailure && (
                  <p className="mt-1 text-[11px] text-amber-300" data-testid="vivarium-auto-feed-last-failure">
                    Last background attempt: {autoFeedLastFailure}
                  </p>
                )}
              </div>
            </div>
            <div className="border border-zinc-800 bg-zinc-950 p-4 rounded-sm">
              <div className="text-[10px] uppercase tracking-widest text-zinc-500">Actions</div>
              <div className="flex gap-2 mt-2">
                <button 
                  onClick={startBreeding} 
                  disabled={busy || data.beast_count < 2} 
                  className="text-[10px] uppercase tracking-wide px-3 py-1.5 border border-amber-700 text-amber-300 hover:bg-amber-900/20 rounded-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                  title={data.beast_count < 2 ? "Need at least 2 beasts" : "Breed two beasts"}
                >
                  <Heart size={12} className="inline mr-1" /> Breed {breedingTickets > 0 && `(${breedingTickets}🎟️)`}
                </button>
              </div>
            </div>
          </div>

          {/* Upkeep and Penalty Guide Section */}
          <div className="border border-zinc-800 bg-zinc-950 p-5 rounded-sm" data-testid="vivarium-upkeep-guide">
            <div className="flex items-center gap-2 mb-4 border-b border-zinc-800 pb-3">
              <h3 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm font-semibold">
                Vivarium Upkeep & Care Guide
              </h3>
            </div>
            
            <div className="grid md:grid-cols-2 gap-6">
              {/* Left Column: How Upkeep Works */}
              <div className="space-y-3">
                <h4 className="text-xs uppercase tracking-widest text-zinc-400 font-semibold">Care & Cost Rules</h4>
                <p className="text-xs text-zinc-300 leading-relaxed">
                  Your beasts require daily care. At the server&apos;s daily check, each unpaid day becomes debt unless your follis covered that day in time:
                </p>
                <ul className="text-xs text-zinc-400 space-y-1.5 pl-1">
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400" />
                    <span><b className="text-slate-300">Silver Tier</b>: 25 follis / day</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                    <span><b className="text-amber-300">Elite Tier</b>: 50 follis / day</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                    <span><b className="text-amber-300">Legendary Tier</b>: 100 follis / day</span>
                  </li>
                </ul>
                <p className="text-xs text-zinc-500 leading-relaxed italic">
                  Turn on **Automatic Feeding** to let the server attempt that day&apos;s payment automatically before debt is added.
                </p>
              </div>

              {/* Right Column: Consequences and Penalties */}
              <div className="space-y-3">
                <h4 className="text-xs uppercase tracking-widest text-rose-400 font-semibold">⚠️ Neglect Penalties & Timeline</h4>
                <p className="text-xs text-zinc-300 leading-relaxed">
                  If you do not have enough follis, or you forget to pay the workers manually, your upkeep debt (unpaid days) will increase. If debt is neglected:
                </p>
                <div className="space-y-2 border-l-2 border-rose-900/50 pl-3">
                  <div className="text-xs">
                    <span className="text-yellow-400 font-semibold">5–9 Unpaid Days</span>: 
                    <span className="text-zinc-400 ml-1">Beasts grow restless but bonuses remain active.</span>
                  </div>
                  <div className="text-xs">
                    <span className="text-orange-400 font-semibold">10–13 Unpaid Days</span>: 
                    <span className="text-zinc-400 ml-1">Beasts grow weak and hungry. Upkeep warning is active in the Ludus.</span>
                  </div>
                  <div className="text-xs">
                    <span className="text-rose-400 font-semibold">14+ Unpaid Days (Starving)</span>: 
                    <span className="text-zinc-400 ml-1">**Critical neglect penalty!** All equipped beast bonuses are **DISABLED** (+0 stats), and they cannot be bred or equipped.</span>
                  </div>
                  <div className="text-xs">
                    <span className="text-rose-500 font-semibold">Prolonged Starvation</span>: 
                    <span className="text-zinc-400 ml-1">If starvation debt continues unpaid, beasts will eventually **abandon your Ludus** and escape.</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Upgrade Section */}
          {data.next_upgrade && (
            <div className="border border-amber-800/40 bg-amber-950/10 rounded-sm p-4 flex items-center justify-between" data-testid="upgrade-prompt">
              <div>
                <p className="text-sm text-amber-300 font-cinzel">Upgrade Vivarium Capacity</p>
                <p className="text-xs text-zinc-400 mt-1">
                  Expand from <b className="text-amber-300">{vivarium.capacity}</b> to <b className="text-amber-300">{data.next_upgrade.new_capacity} beasts</b>
                </p>
              </div>
              <button onClick={openUpgradeModal} className={btn() + " flex items-center gap-2"}>
                <TrendingUp size={14} /> Upgrade
              </button>
            </div>
          )}

          {/* Find Beasts Alert */}
          <div className="border border-amber-800/40 bg-amber-950/10 rounded-sm p-4 flex items-start gap-3" data-testid="find-beasts-alert">
            <BeastIcon size={18} className="text-amber-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-amber-300 font-semibold">Looking for beasts?</p>
              <p className="text-xs text-zinc-400 mt-1">
                Complete <b className="text-amber-300">Beast Hunt</b> quests to capture new beasts for your Vivarium.
              </p>
              <a href="/events?tab=quests" className="inline-block mt-3 px-3 py-1.5 text-xs border border-amber-700 text-amber-300 hover:bg-amber-900/20 rounded-sm transition-colors">
                Go to Quests →
              </a>
            </div>
          </div>

          {/* Breeding Ticket Quests Section */}
          {quests && quests.filter(q => q.repeatable || !q.completed).length > 0 && (
            <div className="border border-amber-800/40 bg-amber-950/10 rounded-sm p-4">
              <div className="flex items-center gap-2 mb-4">
                <Heart size={18} className="text-amber-400" />
                <h3 className="text-sm font-cinzel text-amber-300 font-semibold uppercase tracking-wide">Breeding Ticket Quests</h3>
              </div>
              <p className="text-xs text-zinc-400 mb-4">
                Capture beasts to earn <b className="text-amber-300">free breeding tickets</b>! These quests use your <b className="text-amber-200">lifetime beasts captured</b>, even if beasts are later sold or released.
              </p>
              <div className="space-y-3">
                {quests.filter(q => q.repeatable || !q.completed).map(quest => (
                  <div 
                    key={quest.id} 
                    className={`border rounded-sm p-4 ${quest.completed ? 'border-zinc-800 bg-zinc-900/50 opacity-60' : (quest.can_claim && quest.id !== 'capture_10') ? 'border-amber-700 bg-amber-950/20' : 'border-zinc-700 bg-zinc-900'}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm font-cinzel text-zinc-100">{quest.name}</span>
                          {quest.completed && !quest.repeatable && (
                            <span className="text-[10px] px-2 py-0.5 bg-zinc-800 text-zinc-500 rounded uppercase tracking-wider">
                              <CheckCircle size={10} className="inline mr-1" />Claimed
                            </span>
                          )}
                          {quest.repeatable && quest.times_completed > 0 && (
                            <span className="text-[10px] px-2 py-0.5 bg-amber-950/40 text-amber-300 rounded uppercase tracking-wider border border-amber-800/40">
                              Claimed {quest.times_completed}x
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-zinc-400 mb-2">
                          {quest.repeatable 
                            ? `Capture ${quest.beasts_required} beasts total (every ${quest.beasts_required})` 
                            : `Capture ${quest.beasts_required} beasts total`}
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex-1 bg-zinc-950 rounded-full h-2 overflow-hidden border border-zinc-800">
                            <div 
                              className="h-full transition-all bg-amber-500"
                              style={{ width: `${Math.min((quest.progress / quest.beasts_required) * 100, 100)}%` }}
                            />
                          </div>
                          <span className="text-[10px] text-zinc-500 font-mono">
                            {quest.progress}/{quest.beasts_required}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <div className="text-right">
                          <div className="text-lg font-bold text-amber-300">🎟️ {quest.tickets}</div>
                          <div className="text-[9px] text-zinc-500 uppercase tracking-wider">ticket{quest.tickets > 1 ? 's' : ''}</div>
                        </div>
                        {quest.id === 'capture_10' && (
                          <div className="text-[10px] text-amber-300 font-semibold uppercase tracking-wider flex items-center gap-1">
                            <CheckCircle size={12} /> Auto-Awarded
                          </div>
                        )}
                        {quest.can_claim && !quest.completed && quest.id !== 'capture_10' && (
                          <button
                            onClick={() => claimQuest(quest.id)}
                            disabled={busy}
                            className={btn("primary") + " text-xs px-3 py-1.5 flex items-center gap-1"}
                          >
                            <Sparkles size={12} /> Claim
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Beast List Header */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <h2 className="font-cinzel text-amber-300 uppercase tracking-wide text-lg">Your Beasts</h2>
            <div className="flex items-center gap-2">
              <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}
                className="bg-zinc-900 border border-zinc-800 px-3 py-1.5 text-xs text-zinc-200 rounded-sm">
                <option value="date">Sort: Newest</option>
                <option value="species">Sort: Species</option>
                <option value="rarity">Sort: Rarity</option>
                <option value="stat">Sort: Stat Value</option>
              </select>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}
                className="bg-zinc-900 border border-zinc-800 px-3 py-1.5 text-xs text-zinc-200 rounded-sm">
                <option value="all">All Beasts</option>
                <option value="equipped">Equipped</option>
                <option value="unequipped">Unequipped</option>
              </select>
            </div>
          </div>

          {/* Beast Grid */}
          {sortedBeasts.length === 0 ? (
            <div className="text-center py-12 border border-zinc-800 bg-zinc-950 rounded-sm">
              <BeastIcon size={48} className="text-zinc-700 mx-auto mb-3" />
              <p className="text-sm text-zinc-500">No beasts yet. Complete beast capture quests to find them!</p>
            </div>
          ) : (
            <>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="beast-grid">
              {paginatedBeasts.map((beast) => {
                const tierColor = TIER_COLORS[beast.tier] || "";
                return (
                  <div key={beast.id} data-testid={`beast-${beast.id}`}
                    className={`border rounded-sm p-4 ${tierColor} relative`}>
                    {beast.is_locked && (
                      <div className="absolute top-2 right-2 text-xs flex items-center gap-1 px-2 py-0.5 bg-rose-900/50 border border-rose-700 rounded-sm">
                        <Lock size={10} /> Locked
                      </div>
                    )}
                    {!beast.is_locked && beast.mutations && beast.mutations.length > 0 && (
                      <div className="absolute top-2 right-2 text-xs flex items-center gap-1 px-2 py-0.5 bg-rose-900/50 border border-rose-700 rounded-sm">
                        ⚠️ Negative Traits
                      </div>
                    )}
                    
                    <div className="flex items-start gap-3 mb-3">
                      <BeastIcon size={32} className="text-amber-400" species={beast.species} />
                      <div className="flex-1">
                        <div className="text-sm font-cinzel text-zinc-100">
                          {beast.custom_name || beast.display_name}
                        </div>
                        {beast.custom_name && (
                          <div className="text-[10px] text-zinc-500 italic">{beast.display_name}</div>
                        )}
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-1 flex gap-2 items-center flex-wrap">
                          <span>{beast.tier}</span>
                          <span className="text-zinc-600">·</span>
                          <span className="capitalize text-amber-200/80">{(beast.species || '').replace(/_/g, ' ')}</span>
                          {beast.listed_on_auction && (
                            <>
                              <span className="text-zinc-600">·</span>
                              <span className="px-2 py-0.5 rounded bg-amber-950/30 border border-amber-700 text-amber-300">
                                <Gavel size={10} className="inline mr-1" /> AUCTION
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    <div className="space-y-1.5 mb-3">
                      <div className="text-xs">
                        <span className="text-zinc-500">Primary:</span> <span className="text-amber-300">+{beast.primary_value} {beast.primary_stat}</span>
                        {beast.base_primary_value && beast.base_primary_value !== beast.primary_value && (
                          <span className="text-[10px] text-zinc-500 ml-2">(base: {beast.base_primary_value})</span>
                        )}
                      </div>
                      <div className="text-xs">
                        <span className="text-zinc-500">Affix:</span> <span className="text-zinc-200">+{beast.affix_value}% {beast.affix_stat}</span>
                        {beast.base_affix_value && beast.base_affix_value !== beast.affix_value && (
                          <span className="text-[10px] text-zinc-500 ml-2">(base: {beast.base_affix_value}%)</span>
                        )}
                      </div>
                      {beast.affix_stat_2 && (
                        <div className="text-xs">
                          <span className="text-zinc-500">Affix 2:</span> <span className="text-zinc-200">+{beast.affix_value_2}% {beast.affix_stat_2}</span>
                          {beast.base_affix_value_2 && beast.base_affix_value_2 !== beast.affix_value_2 && (
                            <span className="text-[10px] text-zinc-500 ml-2">(base: {beast.base_affix_value_2}%)</span>
                          )}
                        </div>
                      )}
                      {beast.tier === "legendary" && beast.legendary_custom_affix_stat && (
                        <div data-testid={`legendary-custom-affix-${beast.id}`} className="text-xs border-t border-amber-800/50 pt-1.5 mt-1.5">
                          <span className="text-amber-400">Chosen Affix:</span>{" "}
                          <span className="text-amber-200">+{beast.legendary_custom_affix_value || 5}% {beast.legendary_custom_affix_stat}</span>
                        </div>
                      )}
                    </div>

                    {beast.tier === "legendary" && !beast.legendary_custom_affix_stat && (
                      <button
                        type="button"
                        data-testid={`choose-legendary-affix-${beast.id}`}
                        onClick={() => openLegendaryAffixPicker(beast)}
                        disabled={busy}
                        className="w-full mb-3 px-3 py-2 text-[11px] uppercase tracking-widest border border-amber-600 text-amber-300 bg-amber-950/20 hover:bg-amber-900/30 transition-colors disabled:opacity-50"
                      >
                        Choose Legendary Affix
                      </button>
                    )}

                    {/* Negative Traits Warning Section */}
                    {beast.mutations && beast.mutations.length > 0 && (
                      <div className="mb-3 border border-rose-700/50 bg-rose-950/20 rounded-sm p-2">
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <span className="text-[11px] uppercase tracking-widest text-rose-400 font-semibold">⚠️ Negative Traits</span>
                        </div>
                        <div className="space-y-1">
                          {beast.mutations.includes('frail') && (
                            <div className="text-[10px] text-rose-300">
                              <span className="font-semibold">Frail:</span> -50% primary stat
                            </div>
                          )}
                          {beast.mutations.includes('sluggish') && (
                            <div className="text-[10px] text-rose-300">
                              <span className="font-semibold">Sluggish:</span> -50% affix stat
                            </div>
                          )}
                          {beast.mutations.includes('weak_bloodline') && (
                            <div className="text-[10px] text-rose-300">
                              <span className="font-semibold">Weak Bloodline:</span> Cannot use beast food
                            </div>
                          )}
                          {beast.mutations.includes('genetic_ceiling') && (
                            <div className="text-[10px] text-rose-300">
                              <span className="font-semibold">Genetic Ceiling:</span> Capped at 60 primary stat
                            </div>
                          )}
                          {beast.mutations.includes('stat_swap_bug') && (
                            <div className="text-[10px] text-rose-300">
                              <span className="font-semibold">Stat Swap Bug:</span> Primary and affix swapped
                            </div>
                          )}
                          {beast.mutations.includes('sterile') && (
                            <div className="text-[10px] text-rose-300">
                              <span className="font-semibold">Sterile:</span> Cannot breed
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {/* Breeding Cooldown */}
                    {!beast.can_breed && beast.breed_cooldown_expires_at && (
                      <div className="mb-3 border border-rose-700/50 bg-rose-950/20 rounded-sm p-2">
                        <div className="text-[10px] text-rose-400 flex items-center gap-1">
                          <Heart size={10} /> Breeding cooldown: {Math.ceil((new Date(beast.breed_cooldown_expires_at) - new Date()) / (1000 * 60 * 60 * 24))} days
                        </div>
                        <div className="mt-2 flex items-center gap-2">
                          <button
                            type="button"
                            data-testid={`love-potion-button-${beast.id}`}
                            onClick={() => setLovePotionModal(beast)}
                            disabled={(consumables.love_potion || 0) < 1 || busy}
                            className={`text-[10px] px-2.5 py-1.5 rounded-sm transition-colors border flex items-center gap-1.5 ${
                              (consumables.love_potion || 0) > 0
                                ? 'border-amber-700 bg-amber-950/20 text-amber-300 hover:bg-amber-900/20'
                                : 'border-zinc-800 bg-zinc-900/50 text-zinc-600 cursor-not-allowed'
                            }`}
                            title={(consumables.love_potion || 0) > 0 ? 'Reduce this breeding cooldown by 4 days' : 'No Love Potion in inventory'}
                          >
                            <img src="/love-potion.png" alt="Love Potion" className="w-4 h-4 object-contain" />
                            {(consumables.love_potion || 0) > 0 ? `Use Love Potion (${consumables.love_potion || 0})` : 'No Love Potion'}
                          </button>
                          <span className="text-[10px] text-zinc-500">Removes 4 days from this cooldown.</span>
                        </div>
                      </div>
                    )}

                    {beast.equipped_to && (
                      <div className="mb-3 border border-amber-700/50 bg-amber-950/20 rounded-sm p-2">
                        <div className="text-[10px] text-amber-400 flex items-center gap-1">
                          ⚔️ Equipped to <span className="font-cinzel text-amber-300">{beast.equipped_gladiator_name || "Unknown"}</span>
                        </div>
                        {beast.is_locked && beast.lock_expires_at && (
                          <div className="text-zinc-500 mt-1 flex items-center gap-1">
                            <Lock size={10} /> Locked until {new Date(beast.lock_expires_at).toLocaleString()}
                          </div>
                        )}
                      </div>
                    )}

                    {/* Beast Food Section */}
                    <div className="mb-3 border-t border-zinc-700 pt-2">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Beast Food</span>
                        <span className={`text-[10px] font-semibold ${(beast.food_consumed || 0) >= 3 ? 'text-rose-400' : 'text-amber-300'}`}>
                          {beast.food_consumed || 0}/3 Used
                        </span>
                      </div>
                      
                      {(beast.food_consumed || 0) >= 3 ? (
                        <div className="text-[9px] text-zinc-500 text-center py-2 bg-zinc-900/50 rounded border border-zinc-800">
                          Max food consumed
                        </div>
                      ) : beast.mutations && beast.mutations.includes('weak_bloodline') ? (
                        <div className="text-[9px] text-rose-400 text-center py-2 bg-rose-950/20 rounded border border-rose-800">
                          ⚠️ Weak Bloodline: Cannot use beast food
                        </div>
                      ) : (
                        <div>
                          {/* Show food button for primary stat only */}
                          {beast.primary_stat && (() => {
                            const foodType = `beast_food_${beast.primary_stat}`;
                            const hasFood = (consumables[foodType] || 0) > 0;
                            const label = beast.primary_stat.charAt(0).toUpperCase() + beast.primary_stat.slice(1);
                            return (
                              <button
                                onClick={() => setBeastFoodModal({ beast, food_type: foodType })}
                                disabled={!hasFood || busy}
                                className={`w-full text-xs px-3 py-2 rounded-sm transition-colors border flex items-center justify-center gap-2 ${
                                  hasFood 
                                    ? 'border-amber-700 bg-amber-950/20 text-amber-300 hover:bg-amber-900/20' 
                                    : 'border-zinc-800 bg-zinc-900/50 text-zinc-600 cursor-not-allowed'
                                }`}
                                title={hasFood ? `Feed ${label} Food (+2 ${label})` : `No ${label} Food in inventory`}
                              >
                                <img src="/beast-food-v3.png" alt="Beast Food" className="w-5 h-5 object-contain" />
                                <span>Feed {label}</span>
                              </button>
                            );
                          })()}
                        </div>
                      )}
                    </div>

                    {/* Beast Supplement Section */}
                    <div className="mb-3 border-t border-zinc-700 pt-2">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[10px] text-zinc-500 uppercase tracking-wider">Beast Supplement</span>
                        <span className={`text-[10px] font-semibold ${(beast.supplements_consumed || 0) >= 3 ? 'text-rose-400' : 'text-amber-300'}`}>
                          {beast.supplements_consumed || 0}/3 Used
                        </span>
                      </div>
                      
                      {(() => {
                        const primaryAtCap = Number(beast.affix_value || 0) >= BEAST_AFFIX_CAP;
                        const secondaryAtCap = !beast.affix_stat_2 || Number(beast.affix_value_2 || 0) >= BEAST_AFFIX_CAP;
                        const allAffixesAtCap = primaryAtCap && secondaryAtCap;
                        const nextBoostableAffix = !primaryAtCap ? beast.affix_stat : beast.affix_stat_2;
                        if (allAffixesAtCap) return (
                          <div data-testid={`vivarium-supplement-affix-cap-${beast.id}`} className="text-[10px] text-amber-200 text-center py-2 bg-amber-950/20 rounded border border-amber-800/70">
                            Affix cap reached: all affixes are at +{BEAST_AFFIX_CAP}%. This beast cannot use its remaining supplement.
                          </div>
                        );
                        if ((beast.supplements_consumed || 0) >= 3) return (
                          <div className="text-[9px] text-zinc-500 text-center py-2 bg-zinc-900/50 rounded border border-zinc-800">
                            Max supplements consumed
                          </div>
                        );
                        if (!beast.affix_stat) return (
                          <div className="text-[9px] text-zinc-500 text-center py-2 bg-zinc-900/50 rounded border border-zinc-800">
                            No affix to boost
                          </div>
                        );
                        if (beast.mutations && beast.mutations.includes('weak_bloodline')) return (
                          <div className="text-[9px] text-rose-400 text-center py-2 bg-rose-950/20 rounded border border-rose-800">
                            Weak Bloodline: Cannot use supplements
                          </div>
                        );
                        const hasSupplement = (consumables.beast_supplement || 0) > 0;
                        const hasMultipleAffixes = beast.affix_stat && beast.affix_stat_2;
                        return (
                          <div>
                            <button
                              data-testid={`vivarium-use-supplement-${beast.id}`}
                              onClick={() => {
                                setBeastSupplementModal({ beast });
                                setSelectedAffix(nextBoostableAffix);
                              }}
                              disabled={!hasSupplement || busy}
                              className={`w-full text-xs px-3 py-2 rounded-sm transition-colors border flex items-center justify-center gap-2 ${
                                hasSupplement
                                  ? 'border-amber-700 bg-amber-950/20 text-amber-300 hover:bg-amber-900/20'
                                  : 'border-zinc-800 bg-zinc-900/50 text-zinc-600 cursor-not-allowed'
                              }`}
                              title={hasSupplement ? (hasMultipleAffixes ? `Choose an affix below the +${BEAST_AFFIX_CAP}% cap` : `Boost affix (+1%, max +${BEAST_AFFIX_CAP}%)`) : 'No Beast Supplement in inventory'}
                            >
                              <img src="/beast-supplement.png" alt="Beast Supplement" className="w-5 h-5 object-contain" />
                              <span>{hasMultipleAffixes ? 'Use Supplement' : `Boost ${beast.affix_stat.charAt(0).toUpperCase() + beast.affix_stat.slice(1)} +1%`}</span>
                            </button>
                          </div>
                        );
                      })()}
                    </div>

                    <div className="flex gap-1.5">
                      {!beast.equipped_to && !beast.listed_on_auction && (
                        <button onClick={() => confirmEquip(beast)} disabled={busy}
                          className="flex-1 text-[10px] uppercase tracking-wide px-2 py-1.5 border border-amber-700 text-amber-300 hover:bg-amber-900/20 rounded-sm transition-colors disabled:opacity-40">
                          Equip
                        </button>
                      )}
                      {beast.listed_on_auction && (
                        <div className="flex-1 text-center text-[10px] uppercase tracking-wide px-2 py-1.5 border border-amber-700/50 text-amber-300 rounded-sm cursor-not-allowed bg-amber-950/10">
                          <Gavel size={10} className="inline mr-1" /> Listed
                        </div>
                      )}
                      {beast.equipped_to && !beast.is_locked && (
                        <button onClick={() => unequipBeast(beast.id)} disabled={busy}
                          className="flex-1 text-[10px] uppercase tracking-wide px-2 py-1.5 border border-zinc-700 text-zinc-400 hover:bg-zinc-800 rounded-sm transition-colors disabled:opacity-40">
                          Unequip
                        </button>
                      )}
                      <button data-testid={`beast-rename-button-${beast.id}`} aria-label={`Rename ${beast.custom_name || beast.display_name}`} title="Rename beast" onClick={() => { setRenameModal(beast); setNewName(beast.custom_name || ""); }}
                        disabled={busy} className="px-2 py-1.5 border border-zinc-700 text-zinc-400 hover:bg-zinc-800 rounded-sm transition-colors disabled:opacity-40">
                        <Edit2 size={12} />
                      </button>
                      <button onClick={() => setReleaseConfirm(beast)} disabled={busy || beast.is_locked}
                        className="px-2 py-1.5 border border-zinc-700 text-zinc-400 hover:border-rose-700 hover:text-rose-300 rounded-sm transition-colors disabled:opacity-40">
                        <Trash2 size={12} />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-3 mt-6">
                <button
                  onClick={() => setBeastPage(p => Math.max(1, p - 1))}
                  disabled={beastPage === 1}
                  className={btn("ghost") + " px-4 py-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed"}
                >
                  Previous
                </button>
                <span className="text-sm text-zinc-400">
                  Page {beastPage} of {totalPages} ({totalBeasts} beast{totalBeasts !== 1 ? 's' : ''})
                </span>
                <button
                  onClick={() => setBeastPage(p => Math.min(totalPages, p + 1))}
                  disabled={beastPage === totalPages}
                  className={btn("ghost") + " px-4 py-2 text-sm disabled:opacity-40 disabled:cursor-not-allowed"}
                >
                  Next
                </button>
              </div>
            )}
            </>
          )}
        </>
      )}

      {/* Build Modal */}
      <Modal open={buildModal} onClose={() => setBuildModal(false)} title="Build Vivarium">
        <div className="space-y-4">
          <p className="text-sm text-zinc-300">
            Build a beast enclosure to capture and equip exotic creatures. Beasts grant powerful stat bonuses in <b className="text-amber-300">Ranked PvP only</b>.
          </p>
          
          {/* Common Fragments - Weapon/Armor Split */}
          <div className="border border-zinc-800 bg-zinc-950 rounded-sm p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-amber-300">Common Fragments</span>
              <span className="text-xs text-zinc-500">Need: 500 total</span>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              {/* Weapon */}
              <div className="border border-zinc-700 rounded p-3">
                <div className="text-xs text-zinc-400 mb-2">⚔️ Weapon</div>
                <input
                  type="number"
                  min="0"
                  max={data?.fragments?.weapon?.common || 0}
                  value={buildFragments.common_weapon}
                  onChange={(e) => {
                    const val = Math.max(0, Math.min(parseInt(e.target.value) || 0, data?.fragments?.weapon?.common || 0));
                    setBuildFragments(prev => ({ ...prev, common_weapon: val }));
                  }}
                  className="w-full bg-zinc-900 border border-zinc-700 px-2 py-1 text-sm text-amber-300 rounded"
                />
                <div className="text-[10px] text-amber-500 font-semibold mt-1">
                  Have: {data?.fragments?.weapon?.common || 0}
                </div>
              </div>
              
              {/* Armor */}
              <div className="border border-zinc-700 rounded p-3">
                <div className="text-xs text-zinc-400 mb-2">🛡️ Armor</div>
                <input
                  type="number"
                  min="0"
                  max={data?.fragments?.armor?.common || 0}
                  value={buildFragments.common_armor}
                  onChange={(e) => {
                    const val = Math.max(0, Math.min(parseInt(e.target.value) || 0, data?.fragments?.armor?.common || 0));
                    setBuildFragments(prev => ({ ...prev, common_armor: val }));
                  }}
                  className="w-full bg-zinc-900 border border-zinc-700 px-2 py-1 text-sm text-amber-300 rounded"
                />
                <div className="text-[10px] text-amber-500 font-semibold mt-1">
                  Have: {data?.fragments?.armor?.common || 0}
                </div>
              </div>
            </div>
            
            <div className={`text-xs text-right font-semibold ${(buildFragments.common_weapon + buildFragments.common_armor) === 500 ? 'text-amber-300' : 'text-rose-400'}`}>
              Total: {buildFragments.common_weapon + buildFragments.common_armor} / 500
            </div>
          </div>
          
          {/* Uncommon Fragments - Weapon/Armor Split */}
          <div className="border border-zinc-800 bg-zinc-950 rounded-sm p-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-sm font-semibold text-amber-300">Uncommon Fragments</span>
              <span className="text-xs text-zinc-500">Need: 100 total</span>
            </div>
            
            <div className="grid grid-cols-2 gap-3">
              {/* Weapon */}
              <div className="border border-zinc-700 rounded p-3">
                <div className="text-xs text-zinc-400 mb-2">⚔️ Weapon</div>
                <input
                  type="number"
                  min="0"
                  max={data?.fragments?.weapon?.uncommon || 0}
                  value={buildFragments.uncommon_weapon}
                  onChange={(e) => {
                    const val = Math.max(0, Math.min(parseInt(e.target.value) || 0, data?.fragments?.weapon?.uncommon || 0));
                    setBuildFragments(prev => ({ ...prev, uncommon_weapon: val }));
                  }}
                  className="w-full bg-zinc-900 border border-zinc-700 px-2 py-1 text-sm text-amber-300 rounded"
                />
                <div className="text-[10px] text-amber-500 font-semibold mt-1">
                  Have: {data?.fragments?.weapon?.uncommon || 0}
                </div>
              </div>
              
              {/* Armor */}
              <div className="border border-zinc-700 rounded p-3">
                <div className="text-xs text-zinc-400 mb-2">🛡️ Armor</div>
                <input
                  type="number"
                  min="0"
                  max={data?.fragments?.armor?.uncommon || 0}
                  value={buildFragments.uncommon_armor}
                  onChange={(e) => {
                    const val = Math.max(0, Math.min(parseInt(e.target.value) || 0, data?.fragments?.armor?.uncommon || 0));
                    setBuildFragments(prev => ({ ...prev, uncommon_armor: val }));
                  }}
                  className="w-full bg-zinc-900 border border-zinc-700 px-2 py-1 text-sm text-amber-300 rounded"
                />
                <div className="text-[10px] text-amber-500 font-semibold mt-1">
                  Have: {data?.fragments?.armor?.uncommon || 0}
                </div>
              </div>
            </div>
            
            <div className={`text-xs text-right font-semibold ${(buildFragments.uncommon_weapon + buildFragments.uncommon_armor) === 100 ? 'text-amber-300' : 'text-rose-400'}`}>
              Total: {buildFragments.uncommon_weapon + buildFragments.uncommon_armor} / 100
            </div>
          </div>
          
          <div className="border border-zinc-800 bg-zinc-950 rounded-sm p-4">
            <div className="text-xs text-zinc-500 mb-1">Starting Capacity:</div>
            <div className="text-sm text-zinc-200">3 beasts</div>
          </div>
          
          <div className="flex gap-2 justify-end">
            <button onClick={() => setBuildModal(false)} className={btn("ghost")}>Cancel</button>
            <button 
              onClick={buildVivarium} 
              disabled={busy || (buildFragments.common_weapon + buildFragments.common_armor !== 500) || (buildFragments.uncommon_weapon + buildFragments.uncommon_armor !== 100)} 
              className={btn()}
            >
              <Sparkles size={14} className="mr-2" /> Build for Fragments
            </button>
          </div>
        </div>
      </Modal>

      {/* Upgrade Modal */}
      <Modal open={upgradeModal} onClose={() => setUpgradeModal(false)} title="Upgrade Vivarium">
        {data?.next_upgrade && (
          <div className="space-y-4">
            <p className="text-sm text-zinc-300">
              Expand your Vivarium from <b className="text-amber-300">{vivarium.capacity}</b> to <b className="text-amber-300">{data.next_upgrade.new_capacity} beasts</b>.
            </p>
            
            {/* Check if upgrade requires gold (capacity 15+) */}
            {data.next_upgrade.costs.gold ? (
              /* Gold-based upgrade */
              <div className="border border-amber-800 bg-amber-950/20 p-6 rounded text-center">
                <div className="text-2xl font-bold text-amber-300 mb-2">{data.next_upgrade.costs.gold} Gold</div>
                <p className="text-xs text-zinc-400">
                  You have: <span className={user?.gold >= data.next_upgrade.costs.gold ? "text-amber-300" : "text-rose-400"}>{user?.gold || 0}g</span>
                </p>
              </div>
            ) : (
              /* Fragment-based upgrade (capacity < 15) */
              <div className="space-y-4">
                {Object.entries(data.next_upgrade.costs).map(([rarity, requiredTotal]) => {
                  const weaponAvailable = data?.fragments?.weapon?.[rarity] || 0;
                  const armorAvailable = data?.fragments?.armor?.[rarity] || 0;
                  const weaponValue = upgradeFragments[`${rarity}_weapon`] || 0;
                  const armorValue = upgradeFragments[`${rarity}_armor`] || 0;
                  const currentTotal = weaponValue + armorValue;
                  
                  return (
                    <div key={rarity} className="border border-zinc-800 bg-zinc-950 rounded-sm p-4 space-y-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold text-amber-300 capitalize">{rarity} Fragments</span>
                        <span className="text-xs text-zinc-500">Need: {requiredTotal} total</span>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-3">
                        {/* Weapon */}
                        <div className="border border-zinc-700 rounded p-3">
                          <div className="text-xs text-zinc-400 mb-2">⚔️ Weapon</div>
                          <input
                            type="number"
                            min="0"
                            max={weaponAvailable}
                            value={weaponValue}
                            onChange={(e) => {
                              const val = Math.max(0, Math.min(parseInt(e.target.value) || 0, weaponAvailable));
                              setUpgradeFragments(prev => ({ ...prev, [`${rarity}_weapon`]: val }));
                            }}
                            className="w-full bg-zinc-900 border border-zinc-700 px-2 py-1 text-sm text-amber-300 rounded"
                          />
                          <div className="text-[10px] text-amber-500 font-semibold mt-1">
                            Have: {weaponAvailable}
                          </div>
                        </div>
                        
                        {/* Armor */}
                        <div className="border border-zinc-700 rounded p-3">
                          <div className="text-xs text-zinc-400 mb-2">🛡️ Armor</div>
                          <input
                            type="number"
                            min="0"
                            max={armorAvailable}
                            value={armorValue}
                            onChange={(e) => {
                              const val = Math.max(0, Math.min(parseInt(e.target.value) || 0, armorAvailable));
                              setUpgradeFragments(prev => ({ ...prev, [`${rarity}_armor`]: val }));
                            }}
                            className="w-full bg-zinc-900 border border-zinc-700 px-2 py-1 text-sm text-amber-300 rounded"
                          />
                          <div className="text-[10px] text-amber-500 font-semibold mt-1">
                            Have: {armorAvailable}
                          </div>
                        </div>
                      </div>
                      
                      <div className={`text-xs text-right font-semibold ${currentTotal === requiredTotal ? 'text-amber-300' : 'text-rose-400'}`}>
                        Total: {currentTotal} / {requiredTotal}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            
            <div className="flex gap-2 justify-end">
              <button onClick={() => setUpgradeModal(false)} className={btn("ghost")}>Cancel</button>
              <button 
                onClick={upgradeVivarium} 
                disabled={busy || (data.next_upgrade.costs.gold 
                  ? (user?.gold || 0) < data.next_upgrade.costs.gold
                  : !Object.entries(data.next_upgrade.costs).every(([rarity, required]) => 
                      (upgradeFragments[`${rarity}_weapon`] || 0) + (upgradeFragments[`${rarity}_armor`] || 0) === required
                    )
                )}
                className={btn()}
              >
                <TrendingUp size={14} className="mr-2" /> Upgrade
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Feed Modal */}
      <Modal open={feedModal} onClose={() => setFeedModal(false)} title="Pay Vivarium Upkeep">
        <div className="space-y-4">
          <p className="text-sm text-zinc-300">
            {upkeepTodayDue
              ? "Pay today's upkeep. Any unpaid days remain as debt unless you choose to clear them below."
              : upkeepDays > 0
              ? "Today's upkeep is already paid. Choose how many older unpaid days of debt you want to clear now."
              : "Today's upkeep has already been paid. No additional follis are due right now."}
          </p>

          <div className="border border-zinc-800 bg-zinc-950 rounded-sm p-4 space-y-3" data-testid="vivarium-feed-modal-breakdown">
            <div>
              <div className="text-xs text-zinc-500 mb-2">Daily Upkeep</div>
              <div className="text-sm text-zinc-200">
                {upkeepTierLines.map((line) => (
                  <div key={line.tier}>• {line.count} {line.tier} tier beast{line.count === 1 ? "" : "s"}: {line.total} follis/day</div>
                ))}
                {upkeepTierLines.length === 0 && <div className="text-zinc-500">No beasts require upkeep right now.</div>}
              </div>
            </div>

            <div>
              <div className="text-xs text-zinc-500 mb-2">Day-by-day status</div>
              <div className="space-y-2 max-h-52 overflow-y-auto pr-1" data-testid="vivarium-feed-modal-day-list">
                {upkeepBreakdown.map((entry) => (
                  <div key={`${entry.status}-${entry.day}`} className="flex items-center justify-between gap-3 border border-zinc-800 bg-zinc-900/40 px-3 py-2 rounded-sm">
                    <div>
                      <div className="text-xs text-zinc-200">
                        {entry.label === "Today" ? "Today" : new Date(`${entry.day}T00:00:00`).toLocaleDateString()}
                      </div>
                      <div className={`text-[10px] uppercase tracking-widest mt-1 ${entry.status === "debt" ? "text-rose-300" : "text-amber-300"}`}>
                        {entry.status === "paid" ? "Paid" : entry.status === "due" ? "Due now" : "Debt"}
                      </div>
                    </div>
                    <div className="text-[11px] text-zinc-400 text-right">
                      <div>{entry.follis || 0} follis</div>
                    </div>
                  </div>
                ))}
                {upkeepBreakdown.length === 0 && (
                  <div className="text-xs text-zinc-500">Nothing is currently owed.</div>
                )}
              </div>
            </div>

            {upkeepDays > 0 && (
              <div>
                <label htmlFor="vivarium-clear-debt-days" className="text-xs text-zinc-500 block mb-2" data-testid="vivarium-feed-modal-debt-label">
                  Extra unpaid debt days to clear now
                </label>
                <input
                  id="vivarium-clear-debt-days"
                  type="number"
                  min="0"
                  max={upkeepDays}
                  value={clearDebtDays}
                  onChange={(e) => {
                    const next = Math.max(0, Math.min(upkeepDays, parseInt(e.target.value, 10) || 0));
                    setClearDebtDays(next);
                  }}
                  className="w-full bg-zinc-900 border border-zinc-700 px-3 py-2 text-sm text-amber-300 rounded"
                  data-testid="vivarium-feed-modal-debt-input"
                />
                <p className="mt-2 text-[11px] text-zinc-500">
                  Oldest debt days are cleared first.
                </p>
              </div>
            )}
          </div>

          <div className="border border-amber-900/40 bg-amber-950/10 rounded-sm p-4" data-testid="vivarium-feed-modal-payment-summary">
            <div className="text-xs uppercase tracking-widest text-zinc-500 mb-2">This payment covers</div>
            <div className="text-sm text-zinc-200 space-y-1">
              {upkeepTodayDue && <div>• Today&apos;s upkeep</div>}
              <div>• {clearDebtDays} unpaid debt day{clearDebtDays === 1 ? "" : "s"}</div>
            </div>
            <div className="mt-3 text-xs text-zinc-500">Follis to charge now:</div>
            <div className="mt-1 text-sm text-zinc-200" data-testid="vivarium-feed-modal-payment-costs">
              {payPreviewFollis > 0 ? <div>{payPreviewFollis} follis</div> : <div className="text-zinc-500">No charge.</div>}
            </div>
            <div className="mt-3 text-[11px] text-zinc-500">
              Total still owed across all unpaid days: {totalDueFollis > 0 ? `${totalDueFollis} follis` : "nothing"}
            </div>
          </div>

          <p className="text-xs text-zinc-500" data-testid="vivarium-feed-modal-auto-feed-note">
            Prefer not to manage this manually? Turn on Automatic Feeding and the server will try to pay that day&apos;s upkeep first at the daily check before adding debt.
          </p>
          <div className="flex gap-2 justify-end">
            <button onClick={() => setFeedModal(false)} className={btn("ghost")} data-testid="vivarium-feed-modal-cancel-button">Cancel</button>
            <button onClick={() => feedWorkers(clearDebtDays)} disabled={busy || totalPayDays <= 0} className={btn()} data-testid="vivarium-feed-modal-submit-button">
              Pay Workers
            </button>
          </div>
        </div>
      </Modal>

      {/* Rename Modal */}
      <Modal open={!!renameModal} onClose={() => { setRenameModal(null); setNewName(""); }} title="Rename Beast">
        <div className="space-y-4">
          <div className="bg-amber-900/20 border border-amber-800/40 rounded-sm p-3 mb-3">
            <p className="text-xs text-amber-300 flex items-center gap-2">
              <FollisCoin size={14} />
              <span>{renameModal?.has_been_renamed ? <>Rename cost: <b>50 Follis</b></> : <>Your first rename is <b>free</b>. Later renames cost 50 Follis.</>}</span>
            </p>
          </div>
          
          <div>
            <label className="text-xs text-zinc-500 mb-1 block">New Name</label>
            <input
              data-testid="beast-rename-input"
              type="text"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder="Enter a custom name"
              maxLength={30}
              className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2 text-zinc-200 rounded-sm focus:border-amber-600 outline-none"
            />
          </div>
          <div className="flex gap-2 justify-end">
            <button data-testid="beast-rename-cancel-button" onClick={() => { setRenameModal(null); setNewName(""); }} className={btn("ghost")}>Cancel</button>
            <button data-testid="beast-rename-confirm-button" onClick={renameBeast} disabled={busy || !newName.trim()} className={btn()}>
              <Edit2 size={14} className="mr-2" /> {renameModal?.has_been_renamed ? "Rename · 50 Follis" : "Rename free"}
            </button>
          </div>
        </div>
      </Modal>

      {/* Release Confirm */}
      <Modal open={!!releaseConfirm} onClose={() => setReleaseConfirm(null)} title="Release Beast?">
        {releaseConfirm && (
          <div className="space-y-4">
            <p className="text-sm text-rose-300">
              Release <b>{releaseConfirm.custom_name || releaseConfirm.display_name}</b> permanently? This cannot be undone.
            </p>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setReleaseConfirm(null)} className={btn("ghost")}>Cancel</button>
              <button onClick={releaseBeast} disabled={busy} className="px-3 py-2 text-sm border border-rose-700 text-rose-300 hover:bg-rose-950/30 rounded-sm transition-colors">
                <Trash2 size={14} className="mr-2" /> Release
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Equip Confirmation Modal */}
      <Modal open={!!equipConfirm} onClose={() => setEquipConfirm(null)} title="⚠️ Equip Beast">
        {equipConfirm && (
          <div className="space-y-4">
            <div className="border border-amber-900/40 bg-amber-950/10 rounded-sm p-4">
              <p className="text-sm text-amber-200 mb-3">
                You are about to equip <b className="text-amber-300">{equipConfirm.display_name}</b> to a gladiator.
              </p>
              <div className="border-l-2 border-rose-500 pl-3">
                <p className="text-sm text-zinc-300 font-medium">⚠️ Important:</p>
                <p className="text-xs text-zinc-400 mt-1">
                  Once equipped, this beast will be <b className="text-rose-300">locked to that gladiator for 24 hours</b> and cannot be:
                </p>
                <ul className="text-xs text-zinc-400 mt-2 space-y-1 list-disc list-inside">
                  <li>Transferred to another gladiator</li>
                  <li>Unequipped during the lock period</li>
                  <li>Released or sold on the Auction House</li>
                </ul>
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setEquipConfirm(null)} disabled={busy}
                className={btn("ghost") + " flex-1"}>
                Cancel
              </button>
              <button onClick={proceedToEquip} disabled={busy}
                className={btn("primary") + " flex-1"}>
                I Understand, Continue
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Equip Modal - Gladiator Picker */}
      <Modal open={!!equipModal} onClose={() => { setEquipModal(null); setGladiators(null); }} title={`Equip ${equipModal?.display_name || "Beast"}`} wide>
        {equipModal && (
          <div className="space-y-4">
            <p className="text-sm text-zinc-400">Choose a gladiator to equip this beast:</p>
            
            {!gladiators ? (
              <div className="text-center py-8">
                <RefreshCw size={24} className="text-amber-400 mx-auto animate-spin" />
                <p className="text-xs text-zinc-500 mt-2">Loading gladiators...</p>
              </div>
            ) : gladiators.length === 0 ? (
              <div className="text-center py-8 text-zinc-500">
                <p className="text-sm">No active gladiators available.</p>
                <button onClick={() => { setEquipModal(null); setGladiators(null); }} className={btn("ghost") + " mt-4"}>Close</button>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 max-h-96 overflow-y-auto" data-testid="gladiator-picker">
                {gladiators.map((glad) => {
                  const totalSkill = glad.overall || 0;
                  const equippedBeast = (data?.beasts || []).find((beast) => beast.equipped_to === glad.id);
                  return (
                    <button
                      key={glad.id}
                      data-testid={`gladiator-picker-option-${glad.id}`}
                      onClick={() => equipBeast(glad.id)}
                      disabled={busy || equippedBeast?.is_locked}
                      className="text-left p-3 border border-zinc-800 hover:border-amber-600 bg-zinc-950 hover:bg-zinc-900 rounded-sm transition-colors disabled:opacity-40"
                    >
                      <div className="flex items-start gap-2 mb-2">
                        <div className="flex-1">
                          <div className="text-sm font-cinzel text-zinc-100">{glad.name}</div>
                          <div className="text-[10px] uppercase tracking-widest text-zinc-500">{glad.cls}</div>
                        </div>
                        <div className="text-xs text-amber-300">{totalSkill}</div>
                      </div>
                      <div className="flex items-center gap-2 text-[10px] text-zinc-400">
                        <span>Age {glad.age}</span>
                        <span>·</span>
                        <span className={glad.injured ? "text-rose-400" : "text-amber-300"}>
                          {glad.injured ? "Injured" : "Healthy"}
                        </span>
                      </div>
                      {equippedBeast && (
                        <div data-testid={`gladiator-picker-equipped-beast-${glad.id}`} className={`mt-3 border-l-2 pl-2.5 ${equippedBeast.is_locked ? "border-rose-500/70" : "border-amber-500/70"}`}>
                          <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-amber-300">
                            <BeastIcon size={13} species={equippedBeast.species} />
                            Equipped: {equippedBeast.custom_name || equippedBeast.display_name}
                          </div>
                          <div data-testid={`gladiator-picker-beast-stats-${glad.id}`} className="mt-1 text-xs text-zinc-300">
                            +{equippedBeast.primary_value} {equippedBeast.primary_stat}
                            {equippedBeast.affix_stat && <> · +{equippedBeast.affix_value}% {equippedBeast.affix_stat}</>}
                            {equippedBeast.affix_stat_2 && <> · +{equippedBeast.affix_value_2}% {equippedBeast.affix_stat_2}</>}
                          </div>
                          <div className={`mt-1 text-[10px] ${equippedBeast.is_locked ? "text-rose-300" : "text-amber-300"}`}>
                            {equippedBeast.is_locked ? "Beast lock active — cannot replace yet" : "Equipping will replace this beast"}
                          </div>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </Modal>


      {/* Breeding Modal */}
      <Modal open={breedModal} onClose={() => { setBreedModal(false); setBreedParent1(null); setBreedParent2(null); }} title="🐾 Breed Beasts" wide>
        <div className="space-y-4">
          <div className="border border-amber-900/40 bg-amber-950/10 rounded-sm p-4">
            <p className="text-sm text-amber-200">
              <b>Breed two beasts of the same species</b> to create offspring with averaged stats and a chance for dual affixes!
            </p>
            <ul className="text-xs text-zinc-400 mt-2 space-y-1">
              <li>• Cost: 100g (Silver), 200g (Elite), 300g (Legendary)</li>
              <li>• Offspring inherits averaged primary stat</li>
              <li>• 90% chance for dual affixes from both parents</li>
              <li>• 7-day breeding cooldown per beast</li>
              <li>• Beasts must not be equipped or listed on auction</li>
            </ul>
          </div>

          {/* Parent Selection */}
          <div className="grid grid-cols-2 gap-4">
            {/* Parent 1 */}
            <div className="border border-zinc-800 bg-zinc-950 p-4 rounded-sm">
              <div className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Parent 1</div>
              {breedParent1 ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="text-sm font-cinzel text-zinc-100 mb-1">{breedParent1.custom_name || breedParent1.display_name}</div>
                      <span className={`inline-block px-2 py-1 text-[10px] font-bold uppercase tracking-wider border rounded ${
                        'border-amber-700/60 bg-amber-950/20 text-amber-300'
                      }`}>
                        {breedParent1.tier}
                      </span>
                    </div>
                    <button onClick={() => clearBreedParent(1)} className="text-rose-400 hover:text-rose-300">
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="text-xs text-zinc-400">
                    +{breedParent1.primary_value} {breedParent1.primary_stat}
                    {breedParent1.affix_stat && ` · +${breedParent1.affix_value}% ${breedParent1.affix_stat}`}
                  </div>
                </div>
              ) : (
                <div className="text-xs text-zinc-600">Select first beast below</div>
              )}
            </div>

            {/* Parent 2 */}
            <div className="border border-zinc-800 bg-zinc-950 p-4 rounded-sm">
              <div className="text-xs text-zinc-500 uppercase tracking-wider mb-2">Parent 2</div>
              {breedParent2 ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="text-sm font-cinzel text-zinc-100 mb-1">{breedParent2.custom_name || breedParent2.display_name}</div>
                      <span className={`inline-block px-2 py-1 text-[10px] font-bold uppercase tracking-wider border rounded ${
                        'border-amber-700/60 bg-amber-950/20 text-amber-300'
                      }`}>
                        {breedParent2.tier}
                      </span>
                    </div>
                    <button onClick={() => clearBreedParent(2)} className="text-rose-400 hover:text-rose-300">
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="text-xs text-zinc-400">
                    +{breedParent2.primary_value} {breedParent2.primary_stat}
                    {breedParent2.affix_stat && ` · +${breedParent2.affix_value}% ${breedParent2.affix_stat}`}
                  </div>
                </div>
              ) : (
                <div className="text-xs text-zinc-400">{breedParent1 ? "Select second beast (same species)" : "Select first beast first"}</div>
              )}
            </div>
          </div>

          {/* Breeding Cost Preview */}
          {breedParent1 && breedParent2 && (
            <div className="space-y-3">
              <div className="border border-zinc-800 bg-zinc-950 rounded-sm p-4">
                <div className="text-xs text-zinc-500 uppercase tracking-wider mb-3">Payment Method</div>
                <div className="space-y-2">
                  <button
                    onClick={() => setBreedUseTickets(false)}
                    className={`w-full text-left border rounded-sm p-3 transition-colors ${!breedUseTickets ? 'border-amber-700 bg-amber-950/20' : 'border-zinc-700 bg-zinc-900 hover:border-amber-700/50'}`}
                  >
                    <div className="flex justify-between items-center">
                      <span className="text-sm flex items-center gap-2">
                        <GoldCoin size={16} className="text-amber-400" /> Pay with Gold
                      </span>
                      <span className="text-amber-400 font-semibold">
                        {Math.max(
                          breedParent1.tier === 'silver' ? 100 : breedParent1.tier === 'elite' ? 200 : 300,
                          breedParent2.tier === 'silver' ? 100 : breedParent2.tier === 'elite' ? 200 : 300
                        )}g
                      </span>
                    </div>
                  </button>
                  <button
                    onClick={() => setBreedUseTickets(true)}
                    disabled={breedingTickets < Math.max(
                      breedParent1.tier === 'silver' ? 1 : breedParent1.tier === 'elite' ? 2 : 3,
                      breedParent2.tier === 'silver' ? 1 : breedParent2.tier === 'elite' ? 2 : 3
                    )}
                    className={`w-full text-left border rounded-sm p-3 transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${breedUseTickets ? 'border-amber-700 bg-amber-950/20' : 'border-zinc-700 bg-zinc-900 hover:border-amber-700/50'}`}
                  >
                    <div className="flex justify-between items-center">
                      <span className="text-sm">🎟️ Pay with Tickets</span>
                      <span className="text-amber-400 font-semibold">
                        {Math.max(
                          breedParent1.tier === 'silver' ? 1 : breedParent1.tier === 'elite' ? 2 : 3,
                          breedParent2.tier === 'silver' ? 1 : breedParent2.tier === 'elite' ? 2 : 3
                        )} ticket{Math.max(
                          breedParent1.tier === 'silver' ? 1 : breedParent1.tier === 'elite' ? 2 : 3,
                          breedParent2.tier === 'silver' ? 1 : breedParent2.tier === 'elite' ? 2 : 3
                        ) > 1 ? 's' : ''} 
                        <span className="text-xs text-zinc-500 ml-1">(have: {breedingTickets})</span>
                      </span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Beast Selection Grid */}
          <div className="border border-zinc-800 rounded-sm">
            <div className="flex items-center justify-between p-3 border-b border-zinc-800 bg-zinc-950">
              <div className="text-xs text-zinc-500 uppercase tracking-wider">
                {!breedParent1 ? "Select first parent" : breedParent1 && !breedParent2 ? `Select second parent (${breedParent1.display_name} only)` : "Both parents selected"}
              </div>
              <select 
                value={breedSortBy} 
                onChange={(e) => setBreedSortBy(e.target.value)}
                className="bg-zinc-900 border border-zinc-700 px-2 py-1 text-[10px] text-zinc-300 rounded-sm"
              >
                <option value="species">Sort: Species</option>
                <option value="rarity">Sort: Rarity</option>
                <option value="stat">Sort: Stat Value</option>
              </select>
            </div>
            <div className="max-h-80 overflow-y-auto space-y-2 p-3">
              {(() => {
                // Filter eligible beasts
                let breedableBeasts = sortedBeasts.filter(b => 
                  !b.equipped_to && 
                  !b.listed_on_auction && 
                  b.can_breed &&
                  (breedParent1 ? b.species === breedParent1.species : true) &&
                  b.id !== breedParent1?.id &&
                  b.id !== breedParent2?.id
                );
                
                // Apply breed modal sorting
                if (breedSortBy === "species") {
                  breedableBeasts.sort((a, b) => a.species.localeCompare(b.species));
                } else if (breedSortBy === "rarity") {
                  const order = { legendary: 0, elite: 1, silver: 2 };
                  breedableBeasts.sort((a, b) => order[a.tier] - order[b.tier]);
                } else if (breedSortBy === "stat") {
                  breedableBeasts.sort((a, b) => b.primary_value - a.primary_value);
                }
                
                return breedableBeasts.length === 0 ? (
                  <div className="text-center text-zinc-400 py-8">
                    No eligible beasts available for breeding
                  </div>
                ) : (
                  breedableBeasts.map(beast => {
                    // Determine tier styling
                    const tierClass = 'border-amber-700/60 bg-amber-950/20 text-amber-300';
                    
                    return (
                      <button
                        key={beast.id}
                        onClick={() => selectBreedParent(beast)}
                        disabled={busy || (breedParent1 && breedParent2)}
                        className="w-full text-left border border-zinc-700 hover:border-amber-700 bg-zinc-900 hover:bg-zinc-800 p-3 rounded-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <div className="text-sm font-cinzel text-zinc-100">{beast.custom_name || beast.display_name}</div>
                              <span className={`px-2 py-0.5 text-[9px] font-bold uppercase tracking-wider border rounded ${tierClass}`}>
                                {beast.tier}
                              </span>
                              {beast.mutations && beast.mutations.length > 0 && (
                                <span className="px-2 py-0.5 text-[9px] uppercase tracking-wider border border-rose-700 bg-rose-950/30 text-rose-400 rounded">
                                  ⚠️ Has Negative Traits
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-zinc-400 mt-1">
                              +{beast.primary_value} {beast.primary_stat}
                              {beast.affix_stat && ` · +${beast.affix_value}% ${beast.affix_stat}`}
                            </div>
                            {beast.mutations && beast.mutations.length > 0 && (
                              <div className="text-[10px] text-rose-400 mt-1">
                                ⚠️ Parent has negative traits: 20% offspring inherit chance
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    );
                  })
                );
              })()}
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-2">
            <button onClick={() => { setBreedModal(false); setBreedParent1(null); setBreedParent2(null); }} disabled={busy} className={btn("ghost") + " flex-1"}>
              Cancel
            </button>
            <button 
              onClick={() => setBreedConfirmModal(true)}
              disabled={busy || !breedParent1 || !breedParent2}
              className={btn("primary") + " flex-1"}
            >
              <Heart size={16} /> Continue
            </button>
          </div>
        </div>
      </Modal>

      {/* Breeding Confirmation Modal */}
      <Modal open={breedConfirmModal} onClose={() => setBreedConfirmModal(false)} title="Confirm Breeding">
        {breedParent1 && breedParent2 && (
          <div className="space-y-4">
            <div className="text-center">
              <p className="text-sm text-zinc-300 mb-2">
                Breed <b className="text-amber-300">{breedParent1.custom_name || breedParent1.display_name}</b> and <b className="text-amber-300">{breedParent2.custom_name || breedParent2.display_name}</b>?
              </p>
            </div>
            
            <div className="border-2 border-amber-800 bg-amber-950/20 p-4 rounded">
              <div className="text-sm text-amber-300 font-semibold mb-2">
                {breedUseTickets ? '🎟️ Payment: Breeding Tickets' : '💰 Payment: Gold'}
              </div>
              <div className="text-lg text-zinc-100 font-bold">
                {breedUseTickets ? 
                  `${Math.max(
                    breedParent1.tier === 'silver' ? 1 : breedParent1.tier === 'elite' ? 2 : 3,
                    breedParent2.tier === 'silver' ? 1 : breedParent2.tier === 'elite' ? 2 : 3
                  )} Ticket${Math.max(
                    breedParent1.tier === 'silver' ? 1 : breedParent1.tier === 'elite' ? 2 : 3,
                    breedParent2.tier === 'silver' ? 1 : breedParent2.tier === 'elite' ? 2 : 3
                  ) > 1 ? 's' : ''}` : 
                  `${Math.max(
                    breedParent1.tier === 'silver' ? 100 : breedParent1.tier === 'elite' ? 200 : 300,
                    breedParent2.tier === 'silver' ? 100 : breedParent2.tier === 'elite' ? 200 : 300
                  )} Gold`
                }
              </div>
              {breedUseTickets && (
                <div className="text-xs text-zinc-500 mt-1">
                  You have: {breedingTickets} ticket{breedingTickets !== 1 ? 's' : ''}
                </div>
              )}
            </div>

            <div className="border border-zinc-700 bg-zinc-900 p-3 rounded text-xs text-zinc-400">
              <p className="mb-1">• Offspring will have averaged stats</p>
              <p className="mb-1">• 90% chance for dual affixes</p>
              <p className="mb-1">• 7-day breeding cooldown per parent</p>
              <p>• Small chance for negative traits. Chance is higher if a parent has negative traits.</p>
            </div>

            {/* Admin Force Negative Traits Checkbox */}
            {user?.is_admin && (
              <div className="border border-amber-700/50 bg-amber-950/20 p-3 rounded">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={breedForceMutation}
                    onChange={(e) => setBreedForceMutation(e.target.checked)}
                    className="w-4 h-4 accent-amber-500"
                  />
                  <span className="text-xs text-amber-300 font-semibold">
                    👑 Admin: Force negative traits (100% chance)
                  </span>
                </label>
                {breedForceMutation && (
                  <p className="text-[10px] text-amber-400 mt-1 ml-6">
                    ⚠️ Offspring will have 1-2 negative traits guaranteed
                  </p>
                )}
              </div>
            )}

            <div className="flex gap-2">
              <button onClick={() => setBreedConfirmModal(false)} disabled={busy} className={btn("ghost") + " flex-1"}>
                Cancel
              </button>
              <button 
                onClick={confirmBreeding}
                disabled={busy}
                className={btn("primary") + " flex-1"}
              >
                {busy ? "Breeding..." : "Confirm Breed"}
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Breeding Result Modal */}
      <Modal open={!!breedResult} onClose={() => setBreedResult(null)} title="🐾 Beast Born!">
        {breedResult && (
          <div className="space-y-4">
            <div className="text-center border-2 border-amber-700 bg-amber-950/20 rounded-sm p-6">
              <BeastIcon size={64} className="mx-auto mb-4 text-amber-400" />
              <h3 className="text-2xl font-cinzel text-amber-300 mb-2">
                {breedResult.offspring?.custom_name || breedResult.display_name}
              </h3>
              <div className="inline-block px-3 py-1 bg-zinc-900 border border-zinc-700 rounded text-xs uppercase text-zinc-400 mb-4">
                {breedResult.offspring?.tier}
              </div>
              
              {breedResult.has_dual_affixes && (
                <div className="mt-3 px-4 py-2 bg-amber-950/20 border border-amber-700 rounded-sm">
                  <p className="text-sm text-amber-300 font-bold">✨ DUAL AFFIXES INHERITED! ✨</p>
                </div>
              )}
            </div>

            {/* Parents Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="border border-zinc-800 bg-zinc-950 rounded-sm p-4">
                <div className="text-xs text-zinc-500 uppercase mb-2">Parent 1</div>
                <div className="text-sm font-cinzel text-zinc-100 mb-1">
                  {breedResult.parent1?.custom_name || breedResult.parent1?.display_name}
                </div>
                <div className="text-xs text-zinc-400 space-y-1">
                  <div>+{breedResult.parent1?.primary_value} {breedResult.parent1?.primary_stat}</div>
                  {breedResult.parent1?.affix_stat && (
                    <div className="text-amber-200">+{breedResult.parent1?.affix_value}% {breedResult.parent1?.affix_stat}</div>
                  )}
                </div>
              </div>

              <div className="border border-zinc-800 bg-zinc-950 rounded-sm p-4">
                <div className="text-xs text-zinc-500 uppercase mb-2">Parent 2</div>
                <div className="text-sm font-cinzel text-zinc-100 mb-1">
                  {breedResult.parent2?.custom_name || breedResult.parent2?.display_name}
                </div>
                <div className="text-xs text-zinc-400 space-y-1">
                  <div>+{breedResult.parent2?.primary_value} {breedResult.parent2?.primary_stat}</div>
                  {breedResult.parent2?.affix_stat && (
                    <div className="text-amber-200">+{breedResult.parent2?.affix_value}% {breedResult.parent2?.affix_stat}</div>
                  )}
                </div>
              </div>
            </div>

            {/* Offspring Stats */}
            <div className="border-2 border-amber-700 bg-amber-950/10 rounded-sm p-4">
              <div className="text-sm text-amber-300 font-semibold mb-3">Offspring Stats</div>
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-zinc-400">Primary:</span>
                  <span className="text-amber-300 font-semibold">
                    +{breedResult.offspring?.primary_value} {breedResult.offspring?.primary_stat}
                  </span>
                </div>
                {breedResult.offspring?.affix_stat && (
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">Affix 1:</span>
                    <span className="text-amber-200">
                      +{breedResult.offspring?.affix_value}% {breedResult.offspring?.affix_stat}
                    </span>
                  </div>
                )}
                {breedResult.offspring?.affix_stat_2 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-zinc-400">Affix 2:</span>
                    <span className="text-amber-200">
                      +{breedResult.offspring?.affix_value_2}% {breedResult.offspring?.affix_stat_2}
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Negative Traits Warning */}
            {breedResult.offspring?.mutations && breedResult.offspring.mutations.length > 0 && (
              <div className="border-2 border-rose-700 bg-rose-950/30 rounded-sm p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm text-rose-400 font-semibold">⚠️ NEGATIVE TRAITS DETECTED</span>
                </div>
                <div className="space-y-1.5">
                  {breedResult.offspring.mutations.map((mutation, idx) => {
                    const mutationInfo = {
                      frail: { name: "Frail", desc: "-50% primary stat" },
                      sluggish: { name: "Sluggish", desc: "-50% affix stat" },
                      weak_bloodline: { name: "Weak Bloodline", desc: "Cannot use beast food" },
                      genetic_ceiling: { name: "Genetic Ceiling", desc: "Capped at 60 primary stat" },
                      stat_swap_bug: { name: "Stat Swap Bug", desc: "Primary and affix swapped" },
                      sterile: { name: "Sterile", desc: "Cannot breed" }
                    };
                    const info = mutationInfo[mutation] || { name: mutation, desc: "" };
                    
                    return (
                      <div key={idx} className="text-xs bg-rose-900/20 border border-rose-800/40 rounded px-3 py-2">
                        <span className="text-rose-300 font-semibold">{info.name}:</span>{" "}
                        <span className="text-rose-400">{info.desc}</span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="text-center">
              <p className="text-xs text-zinc-500 mb-3">Your new beast awaits in the Vivarium!</p>
              <button onClick={() => setBreedResult(null)} className={btn("primary") + " w-full"}>
                Awesome!
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Beast Food Confirmation Modal */}
      <Modal open={!!lovePotionModal} onClose={() => setLovePotionModal(null)} title="💙 Use Love Potion">
        {lovePotionModal && (
          <div className="space-y-4" data-testid="love-potion-vivarium-modal">
            <div className="border border-amber-900/40 bg-amber-950/10 rounded-sm p-4">
              <p className="text-sm text-amber-200 mb-3">
                Use Love Potion on <b className="text-amber-300">{lovePotionModal.custom_name || lovePotionModal.display_name}</b>
              </p>
              <div className="flex items-center gap-3 p-3 bg-zinc-900 rounded border border-zinc-800">
                <img src="/love-potion.png" alt="Love Potion" className="w-12 h-12 object-contain" />
                <div className="flex-1">
                  <div className="text-sm font-cinzel text-zinc-100">Love Potion</div>
                  <div className="text-xs text-amber-300 mt-1">Reduce breeding cooldown by 4 days</div>
                </div>
              </div>
            </div>

            <div className="border-l-2 border-amber-500 pl-3" data-testid="love-potion-current-cooldown">
              <p className="text-xs text-zinc-400">
                • Current cooldown: <b className="text-rose-300">{getRemainingCooldownDays(lovePotionModal.breed_cooldown_expires_at)} day{getRemainingCooldownDays(lovePotionModal.breed_cooldown_expires_at) === 1 ? '' : 's'}</b><br />
                • After potion: <b className="text-amber-300">{Math.max(0, getRemainingCooldownDays(lovePotionModal.breed_cooldown_expires_at) - 4)} day{Math.max(0, getRemainingCooldownDays(lovePotionModal.breed_cooldown_expires_at) - 4) === 1 ? '' : 's'}</b><br />
                • If less than 4 days remain, the cooldown ends immediately
              </p>
            </div>

            <div className="flex gap-2">
              <button onClick={() => setLovePotionModal(null)} disabled={busy} className={btn("ghost") + " flex-1"}>
                Cancel
              </button>
              <button onClick={useLovePotion} data-testid="love-potion-vivarium-confirm" disabled={busy} className={btn("primary") + " flex-1"}>
                Use Love Potion
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Beast Food Confirmation Modal */}
      <Modal open={!!beastFoodModal} onClose={() => setBeastFoodModal(null)} title="🍖 Feed Beast">
        {beastFoodModal && (
          <div className="space-y-4">
            <div className="border border-amber-900/40 bg-amber-950/10 rounded-sm p-4">
              <p className="text-sm text-amber-200 mb-2">
                Feed <b className="text-amber-300">{beastFoodModal.beast.custom_name || beastFoodModal.beast.display_name}</b> with:
              </p>
              <div className="flex items-center gap-3 p-3 bg-zinc-900 rounded border border-zinc-800">
                <img src="/beast-food-v3.png" alt="Beast Food" className="w-12 h-12 object-contain" />
                <div className="flex-1">
                  <div className="text-sm font-cinzel text-zinc-100">
                    Beast Food ({beastFoodModal.food_type.replace('beast_food_', '').charAt(0).toUpperCase() + beastFoodModal.food_type.replace('beast_food_', '').slice(1)})
                  </div>
                  <div className="text-xs text-amber-300 mt-1">
                    +2 {beastFoodModal.food_type.replace('beast_food_', '').charAt(0).toUpperCase() + beastFoodModal.food_type.replace('beast_food_', '').slice(1)} (Permanent)
                  </div>
                </div>
              </div>
            </div>
            
            <div className="border-l-2 border-amber-500 pl-3">
              <p className="text-xs text-zinc-400">
                • Permanently increases beast stat by <b className="text-amber-300">+2</b><br />
                • Food consumed: <b className="text-amber-300">{(beastFoodModal.beast.food_consumed || 0) + 1}/3</b><br />
                • This action cannot be undone
              </p>
            </div>
            
            <div className="flex gap-2">
              <button onClick={() => setBeastFoodModal(null)} disabled={busy}
                className={btn("ghost") + " flex-1"}>
                Cancel
              </button>
              <button onClick={feedBeastFood} disabled={busy}
                className={btn("primary") + " flex-1"}>
                Feed Beast
              </button>
            </div>
          </div>
        )}
      </Modal>

      {/* Beast Supplement Confirmation Modal */}
      <Modal open={!!beastSupplementModal} onClose={() => { setBeastSupplementModal(null); setSelectedAffix(null); }} title="⚗️ Use Beast Supplement">
        {beastSupplementModal && (
          <div className="space-y-4">
            <div className="border border-amber-900/40 bg-amber-950/10 rounded-sm p-4">
              <p className="text-sm text-amber-200 mb-3">
                Use Beast Supplement on <b className="text-amber-300">{beastSupplementModal.beast.custom_name || beastSupplementModal.beast.display_name}</b>
              </p>
              
              {/* Choose which affix to boost */}
              <div className="mb-3">
                <p className="text-xs text-zinc-400 mb-2">Choose which affix to boost:</p>
                <div className="grid grid-cols-2 gap-2">
                  {/* First Affix */}
                  {beastSupplementModal.beast.affix_stat && (
                    <button
                      data-testid="vivarium-supplement-affix-primary"
                      onClick={() => setSelectedAffix(beastSupplementModal.beast.affix_stat)}
                      disabled={supplementPrimaryAtCap}
                      className={`p-3 rounded border transition-all ${supplementPrimaryAtCap ? 'cursor-not-allowed border-zinc-800 bg-zinc-900/50 text-zinc-600' : 
                        selectedAffix === beastSupplementModal.beast.affix_stat
                          ? 'border-amber-500 bg-amber-950/20 text-amber-200'
                          : 'border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-amber-700'}
                      }`}
                    >
                      <div className="text-xs font-semibold mb-1">
                        {beastSupplementModal.beast.affix_stat.charAt(0).toUpperCase() + beastSupplementModal.beast.affix_stat.slice(1)}
                      </div>
                      <div className="text-[10px]">
                        {beastSupplementModal.beast.affix_value}% → {supplementPrimaryAtCap ? `${BEAST_AFFIX_CAP}% cap` : `${beastSupplementModal.beast.affix_value + 1}%`}
                      </div>
                    </button>
                  )}
                  
                  {/* Second Affix (if exists) */}
                  {beastSupplementModal.beast.affix_stat_2 && (
                    <button
                      data-testid="vivarium-supplement-affix-secondary"
                      onClick={() => setSelectedAffix(beastSupplementModal.beast.affix_stat_2)}
                      disabled={supplementSecondaryAtCap}
                      className={`p-3 rounded border transition-all ${supplementSecondaryAtCap ? 'cursor-not-allowed border-zinc-800 bg-zinc-900/50 text-zinc-600' : 
                        selectedAffix === beastSupplementModal.beast.affix_stat_2
                          ? 'border-amber-500 bg-amber-950/20 text-amber-200'
                          : 'border-zinc-700 bg-zinc-900 text-zinc-400 hover:border-amber-700'}
                      }`}
                    >
                      <div className="text-xs font-semibold mb-1">
                        {beastSupplementModal.beast.affix_stat_2.charAt(0).toUpperCase() + beastSupplementModal.beast.affix_stat_2.slice(1)}
                      </div>
                      <div className="text-[10px]">
                        {beastSupplementModal.beast.affix_value_2}% → {supplementSecondaryAtCap ? `${BEAST_AFFIX_CAP}% cap` : `${beastSupplementModal.beast.affix_value_2 + 1}%`}
                      </div>
                    </button>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-3 p-3 bg-zinc-900 rounded border border-zinc-800">
                <img src="/beast-supplement.png" alt="Beast Supplement" className="w-12 h-12 object-contain" />
                <div className="flex-1">
                  <div className="text-sm font-cinzel text-zinc-100">
                    Beast Supplement
                  </div>
                  {selectedAffix && (
                    <div className="text-xs text-amber-300 mt-1">
                      +1% {selectedAffix.charAt(0).toUpperCase() + selectedAffix.slice(1)} Bonus (Permanent)
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {selectedAffix && (
              <div className="border-l-2 border-amber-500 pl-3">
                <p className="text-xs text-zinc-400">
                  {/* Show current and new values for selected affix */}
                  {selectedAffix === beastSupplementModal.beast.affix_stat ? (
                    <>
                      • Current: <b className="text-amber-300">{beastSupplementModal.beast.affix_value}% {selectedAffix}</b><br />
                      • New: <b className="text-amber-300">{selectedSupplementAtCap ? `+${BEAST_AFFIX_CAP}% cap` : `${beastSupplementModal.beast.affix_value + 1}% ${selectedAffix}`}</b><br />
                    </>
                  ) : (
                    <>
                      • Current: <b className="text-amber-300">{beastSupplementModal.beast.affix_value_2}% {selectedAffix}</b><br />
                      • New: <b className="text-amber-300">{selectedSupplementAtCap ? `+${BEAST_AFFIX_CAP}% cap` : `${beastSupplementModal.beast.affix_value_2 + 1}% ${selectedAffix}`}</b><br />
                    </>
                  )}
                  • Supplements used: <b className="text-amber-300">{(beastSupplementModal.beast.supplements_consumed || 0) + 1}/3</b><br />
                  • This action cannot be undone
                </p>
                {selectedSupplementAtCap && <p data-testid="vivarium-supplement-modal-cap-note" className="mt-2 text-xs text-amber-300">This affix is already at the +{BEAST_AFFIX_CAP}% cap.</p>}
              </div>
            )}
            
            <div className="flex gap-2">
              <button onClick={() => { setBeastSupplementModal(null); setSelectedAffix(null); }} disabled={busy}
                className={btn("ghost") + " flex-1"}>
                Cancel
              </button>
              <button data-testid="vivarium-supplement-confirm-button" onClick={useBeastSupplement} disabled={busy || !selectedAffix || selectedSupplementAtCap}
                className={btn("primary") + " flex-1"}>
                Use Supplement
              </button>
            </div>
          </div>
        )}
      </Modal>


      {/* Purchase Confirmation Dialog */}
      <Modal open={!!legendaryAffixModal} onClose={busy ? () => {} : () => setLegendaryAffixModal(null)} title="Choose Legendary Affix">
        {legendaryAffixModal && (
          <div data-testid="legendary-affix-picker-modal">
            <p className="text-sm text-zinc-300 leading-relaxed">Choose one permanent +5% affix for {legendaryAffixModal.custom_name || legendaryAffixModal.display_name}. This choice cannot be changed and will not transfer through breeding.</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-5" data-testid="legendary-affix-options">
              {LEGENDARY_CUSTOM_AFFIX_OPTIONS.map((stat) => (
                <button
                  key={stat}
                  type="button"
                  data-testid={`legendary-affix-option-${stat}`}
                  onClick={() => setLegendaryAffixStat(stat)}
                  disabled={busy}
                  className={`px-3 py-2.5 text-xs uppercase tracking-widest border transition-colors ${legendaryAffixStat === stat ? "border-amber-400 bg-amber-900/30 text-amber-200" : "border-zinc-700 text-zinc-400 hover:border-amber-700 hover:text-zinc-200"}`}
                >
                  +5% {stat}
                </button>
              ))}
            </div>
            <div className="flex justify-end gap-2 mt-6">
              <button type="button" data-testid="legendary-affix-picker-cancel" disabled={busy} onClick={() => setLegendaryAffixModal(null)} className={btn("ghost")}>Cancel</button>
              <button
                type="button"
                data-testid="legendary-affix-picker-continue"
                disabled={busy || !legendaryAffixStat}
                onClick={() => {
                  setLegendaryAffixConfirm({ beast: legendaryAffixModal, stat: legendaryAffixStat });
                  setLegendaryAffixModal(null);
                }}
                className={btn("primary")}
              >
                Continue
              </button>
            </div>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        state={legendaryAffixConfirm && {
          title: "Confirm Legendary Affix",
          message: `Apply +5% ${legendaryAffixConfirm.stat} to ${legendaryAffixConfirm.beast.custom_name || legendaryAffixConfirm.beast.display_name}? This choice is permanent and cannot transfer through breeding.`,
          confirmLabel: "Assign Affix",
          onConfirm: assignLegendaryAffix,
        }}
        busy={busy}
        onClose={() => setLegendaryAffixConfirm(null)}
      />

      <ConfirmDialog
        state={purchaseConfirm}
        busy={busy}
        onClose={() => setPurchaseConfirm(null)}
      />

      </div>  {/* End of space-y-6 wrapper */}
    </div>
  );
}

